import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ChangeDetectorRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ListboxModule } from 'primeng/listbox';
import { DatePickerModule } from 'primeng/datepicker';
import { Router } from '@angular/router';
import { DataService, Note } from '../../_service/data.service';

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import ListItem from '@tiptap/extension-list-item';
import { TaskItemCustom } from './extensions/task-item-custom';
import { BulletListNoShortcut } from './extensions/bullet-list-no-shortcut';
import { TaskListNoShortcut } from './extensions/task-list-no-shortcut';
import {
    OrderedListWithStart,
    restartOrderedList,
    toggleBulletListSafely,
    toggleOrderedListSafely,
} from './extensions/ordered-list-with-start';
import { toggleTaskListSafely } from './extensions/list-type-conversion';
import { ParagraphIndent, indentAtCursor, outdentAtCursor } from './extensions/paragraph-indent';
import { BackgroundColor, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { CellSelection } from 'prosemirror-tables';
import { TableTouchResize } from './extensions/table-touch-resize';
import { TableColumnAutoFit } from './extensions/table-column-autofit';
import { TextSelection, NodeSelection, Transaction } from 'prosemirror-state';
import { DOMParser as ProseMirrorDOMParser, DOMSerializer, Fragment, Node as ProseMirrorNode } from 'prosemirror-model';
import Gapcursor from '@tiptap/extension-gapcursor';
import { CollapsibleSection, CollapsibleTitle, CollapsibleContent } from './extensions/collapsible-section';
import { NoteLink } from './extensions/note-link';
import { Phase1Image } from './extensions/phase1-image';
import { isolateSelectedLines } from './extensions/heading-range';
import { ImageDrop, ImageDropHandlers } from './extensions/image-drop';
import { NoteLinkDialog2Component } from './note-link-dialog2.component';
import { ImageInsertDialogComponent } from './image-insert-dialog.component';
import { ImageEditDialogComponent, ImageEditAttrs } from './image-edit-dialog.component';
import { ImageCropDialogComponent, ImageCropResult } from './image-crop-dialog.component';
import { ImageCrop, imageCropLayout, normalizeImageCrop } from './extensions/image-crop.util';
import { AttachmentImageInsertPayload, LinkedImageInsertPayload } from './extensions/phase1-image';
import { ImageService } from '../../_service/image.service';
import { FileUploadService } from '../../_service/file-upload.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { KeyboardShortcuts } from './extensions/keyboard-shortcuts';
import { CodeBlockWithCopy } from './extensions/code-block-with-copy';
import { BlockTypography } from './extensions/block-typography';
import { LayoutService } from '../../_service/layout.service';
import { NotePrintExportService } from '../../_service/note-print-export.service';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { replaceAttachmentIdInHtml } from '../../_util/attachment-ref.util';
import {
    INLINE_TASK_EVENT,
    InlineTask,
    InlineTaskEventDetail,
    InlineTaskTrigger,
    allInlineTasks,
    deleteInlineTask,
    findInlineTask,
    hasInlineTasks,
    inlineTaskStorage,
    insertInlineTask,
    refreshInlineTaskViews,
    revealInlineTaskErrors,
    updateInlineTask,
} from './extensions/inline-task';
import { TaskAssigneeService, TaskAssigneeSource } from '../../_service/task-assignee.service';
import {
    InlineTaskIssue,
    TaskAssignee,
    addDays,
    isEligibleTaskOwner,
    taskDueDateToDate,
    taskOwnerName,
    toTaskDueDate,
    validateInlineTasks,
} from '../../_util/inline-task.util';
import { ColorSwatchPickerComponent, ColorSelectedEvent } from '../../_shared/ui/color-swatch-picker/color-swatch-picker.component';
import { PASTEL_COLORS, NEUTRAL_COLORS, SwatchColor } from '../../_shared/ui/color-swatch-picker/color-palette.constants';

const COLLAPSIBLE_STATE_STORAGE_KEY = 'richEditor2.collapsibleState';
const COLLAPSIBLE_NOTE_RETENTION_LIMIT = 75;

interface PersistedCollapsibleElementState {
    expanded: boolean;
    updatedAt: string;
}

interface PersistedCollapsibleNoteState {
    lastAccessed: string;
    elements: Record<string, PersistedCollapsibleElementState>;
}

interface PersistedCollapsibleState {
    notes: Record<string, PersistedCollapsibleNoteState>;
}

type CopiedStyleListType = 'bullet' | 'ordered' | 'task' | null;
type CopiedStyleAlign = 'left' | 'center' | 'right' | 'justify' | null;

interface CopiedStyleBuffer {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    textColor: string | null;
    backgroundColor: string | null;
    fontSizePx: number | null;
    headingLevel: 1 | 2 | 3 | 4 | 5 | 6 | null;
    listType: CopiedStyleListType;
    orderedListStart: number | null;
    isCodeBlock: boolean;
    textAlign: CopiedStyleAlign;
}

interface TaskOwnerOption {
    id: string | null;
    name: string;
    /** The task's current owner, who is no longer a member of the note's Space. */
    ineligible?: boolean;
}

const CLEANUP_TABLE_HEADER_BACKGROUND = '#dbeafe';

interface FontFamilyOption {
    label: string;
    value: string;
}

const FONT_FAMILY_OPTIONS: FontFamilyOption[] = [
    { label: 'Default', value: '' },
    { label: 'Sans Serif', value: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    { label: 'Serif', value: 'Georgia, "Times New Roman", Times, serif' },
    { label: 'Monospace', value: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { label: 'Comic Sans', value: '"Comic Sans MS", "Comic Sans", cursive' },
];

@Component({
    selector: 'app-rich-editor',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, InputTextModule, ButtonModule, TooltipModule, ListboxModule, DatePickerModule, NoteLinkDialog2Component, ColorSwatchPickerComponent, ImageInsertDialogComponent, ImageEditDialogComponent, ImageCropDialogComponent],
    templateUrl: './rich-editor.component.html',
    host: {
        'class': 'w-full min-w-0',
        '[class.block]': '!fillHeight',
        '[class.flex]': 'fillHeight',
        '[class.flex-col]': 'fillHeight',
        '[class.h-full]': 'fillHeight',
        '[class.min-h-0]': 'fillHeight',
        '[style.overflow]': "'visible'",
    },
})
export class RichEditorComponent implements AfterViewInit, OnDestroy, OnChanges {

    private readonly defaultFontSizePx = 12;
    private readonly zoomAwareFontSizeVar = 'calc(var(--fontScaleRatio, 1) * var(--editorDeviceScale, 1))';

    static clearPersistedCollapsibleStateForNote(noteId: string | null | undefined): void {
        const trimmedNoteId = (noteId ?? '').trim();
        if (!trimmedNoteId || typeof localStorage === 'undefined') {
            return;
        }

        const raw = localStorage.getItem(COLLAPSIBLE_STATE_STORAGE_KEY);
        if (!raw) {
            return;
        }

        try {
            const parsed = JSON.parse(raw) as PersistedCollapsibleState;
            if (!parsed || typeof parsed !== 'object' || typeof parsed.notes !== 'object' || parsed.notes === null) {
                return;
            }

            const noteStorageKey = `note:${trimmedNoteId}`;
            if (!(noteStorageKey in parsed.notes)) {
                return;
            }

            delete parsed.notes[noteStorageKey];
            localStorage.setItem(COLLAPSIBLE_STATE_STORAGE_KEY, JSON.stringify(parsed));
        } catch {
            // Ignore malformed persistence payloads.
        }
    }

    @Input() editorId: string = 'default-editor';
    @Input() placeholderText: string = 'Text eingeben…';
    @Input() content: string = '';
    @Input() readOnly: boolean = false;
    @Input() delta: unknown | null = null;
    @Input() noteId: string | null = null;
    @Input() spaceId?: string; // the Space noteId lives in, so attachments land in/are read from the right CouchDB database
    /** When true, the editor grows to fill its container's height instead of sizing to its content
     * (a 160px floor still applies), so clicking below the last line of short content still places
     * the cursor at the end of the document instead of hitting empty space outside the editor. The
     * host relies on its own ancestors providing a definite height (e.g. a `flex-1 min-h-0` chain up
     * to a viewport-relative `min-height`); it degrades harmlessly to content-sized when they don't. */
    @Input() fillHeight: boolean = false;

    /** The HTML this editor instance itself most recently produced (set in
     * onUpdate, and after any programmatic setContent()). Lets ngOnChanges tell
     * "the bound [content] input changed because our own edit round-tripped
     * back through the parent's form" apart from a genuinely external content
     * change (sync/conflict resolution, switching notes, discard-restore) —
     * see ngOnChanges for why that distinction matters. */
    private lastKnownEditorHtml: string | null = null;

    @Output() contentChange = new EventEmitter<string>();
    @Output() contentUpdated = new EventEmitter<string>();
    @Output() deltaChange = new EventEmitter<unknown>();
    @Output() deltaUpdated = new EventEmitter<unknown>();
    @Output() saveShortcutTriggered = new EventEmitter<void>();
    @Output() noteLinkInserted = new EventEmitter<string>();
    /** An attachment image just inserted (Insert Image dialog or drag & drop), so the host can record
     * it in the note's Attachments and list it in the Attachments tab. */
    @Output() attachmentImageInserted = new EventEmitter<AttachmentImageInsertPayload>();

    @ViewChild('editorHost', { static: true })
    private editorHost!: ElementRef<HTMLElement>;

    editor: Editor | null = null;
    currentTextColor: string | null = null;
    currentBackgroundColor: string | null = null;
    currentFontFamily = '';
    currentFontSize = '';
    currentHeadingLevel = '';
    readonly fontFamilyOptions = FONT_FAMILY_OPTIONS;
    hasExplicitFontSize = false;
    isBoldActive = false;
    isItalicActive = false;
    isUnderlineActive = false;
    isStrikeActive = false;
    isBulletListActive = false;
    isTaskListToolbarActive = false;
    isOrderedListActive = false;
    isRestartOrderedListActive = false;
    styleTransferMode: 'copy' | 'paste' = 'copy';
    isStyleTransferSticky = false;
    canUndo = false;
    canRedo = false;
    isTableMenuOpen = false;
    collapsibleSectionMenuVisible = false;
    collapsibleSectionMenuPosition = { top: 0, left: 0 };
    /** The menu opens on mousedown; the click that ends that same press must not close it again — it
     * can land outside the button (e.g. on the menu itself), and then reaches onDocumentClick. */
    private collapsibleMenuOpeningPress = false;
    private activeCollapsibleSectionPos: number | null = null;

    imageNodeMenuVisible = false;
    imageNodeMenuExpanded = false;
    imageNodeMenuPosition = { top: 0, left: 0 };
    imageNodeMenuPanelPosition = { top: 0, left: 0 };
    private activeImagePos: number | null = null;
    private activeImageElement: HTMLImageElement | null = null;

    // Image insert / edit dialog properties
    imageInsertDialogVisible = false;
    imageEditDialogVisible = false;
    imageEditAttrs: ImageEditAttrs = {};
    imageEditNaturalWidth: number | null = null;
    imageEditNaturalHeight: number | null = null;
    imageEditFileSizeBytes: number | null = null;
    imageEditFilename: string | null = null;
    private imageEditPos: number | null = null;
    imageCropDialogVisible = false;
    imageCropSrc: string | null = null;
    imageCropInitial: ImageCrop | null = null;
    private imageCropPos: number | null = null;
    private attachmentImageObjectUrls = new Map<string, string>();
    private attachmentImageRequests = new Set<string>();
    private attachmentImageLoadErrorsShown = new Set<string>();
    private attachmentImageRefreshScheduled = false;

    // Inline tasks: eligible owners and the owner / due-date pickers
    private readonly taskAssigneeService = inject(TaskAssigneeService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private taskAssigneeSubscription?: Subscription;
    private taskAssigneesRequested = false;
    taskAssignees: TaskAssignee[] | null = null;
    taskAssigneeSource: TaskAssigneeSource | null = null;
    private taskPickerTaskId: string | null = null;
    taskOwnerDialogVisible = false;
    taskOwnerOptions: TaskOwnerOption[] = [];
    taskOwnerSelection: string | null = null;
    taskOwnerCurrentId: string | null = null;
    private taskOwnerTrigger: InlineTaskTrigger = 'click';
    private taskOwnerApplied = false;
    taskDueDialogVisible = false;
    taskDueDraft: Date | null = null;
    taskDueCurrent: string | null = null;

    // Note link properties
    noteLinkDialogVisible = false;
    allNotes: Note[] = [];
    notesLoading = false;
    selectedTextForNoteLink = '';

    // Web link dialog properties
    linkDialogVisible = false;
    linkLabel = '';
    linkUrl = 'https://';
    private linkSelection: { from: number; to: number } | null = null;


    // Primary color for toolbar icons
    toolbarIconColor = '#1f2937'; // Default dark gray

    private noteLinkDialogHandler?: () => void;
    private linkDialogHandler?: () => void;
    private saveNoteShortcutHandler?: () => void;
    private insertCollapsibleShortcutHandler?: () => void;
    private editorClickHandler?: (event: MouseEvent) => void;
    private inlineTaskEventHandler?: (event: Event) => void;
    private activeResizeCueCell: HTMLElement | null = null;
    private isTableResizeDragging = false;
    private activeResizeEdge: 'left' | 'right' = 'right';
    private resizeIndicatorEl: HTMLDivElement | null = null;
    private readonly resizeEdgeThresholdPx = 10;
    private readonly onEditorMouseMove = (event: MouseEvent) => this.updateTableResizeCue(event);
    private readonly onEditorMouseLeave = () => this.clearTableResizeCue();
    private readonly onEditorMouseDown = (event: MouseEvent) => this.handleTableResizeMouseDown(event);
    private readonly onDocumentMouseMove = (event: MouseEvent) => this.handleTableResizeMouseMove(event);
    private readonly onDocumentMouseUp = () => this.handleTableResizeMouseUp();
    private copiedStyleBuffer: CopiedStyleBuffer | null = null;
    private styleTransferSingleClickTimeoutId: number | null = null;
    private styleTransferApplyTimeoutId: number | null = null;
    private styleTransferSelectionArmed = false;
    private styleTransferLastAppliedSelectionKey: string | null = null;
    private styleTransferSourceSelectionKey: string | null = null;
    private styleTransferSourceRange: { from: number; to: number } | null = null;
    private styleTransferActivationSelectionKey: string | null = null;
    private destroy$ = new Subject<void>();

    // Cell color palettes – sourced from shared colour-swatch constants
    readonly cellColorPastel: SwatchColor[] = PASTEL_COLORS;
    readonly cellColorNeutral: SwatchColor[] = NEUTRAL_COLORS;

    constructor(
        private router: Router,
        private dataService: DataService,
        private layoutService: LayoutService,
        private ngZone: NgZone,
        private notePrintExportService: NotePrintExportService,
        private imageService: ImageService,
        private fileUploadService: FileUploadService,
        private snackbarService: SnackbarService,
        private environmentService: EnvironmentService,
    ) { }

    ngAfterViewInit(): void {
        // Subscribe to layout/primary color changes
        this.syncToolbarIconColor();
        this.layoutService.appStateUpdate$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.syncToolbarIconColor();
            });

        // Offline (or server-unreachable) embedded-image load failures are suppressed
        // below rather than shown to the user; retry them silently once connectivity
        // returns instead of leaving images blank until the next edit/note switch.
        this.environmentService.online$
            .pipe(
                distinctUntilChanged(),
                filter((online) => online),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.scheduleAttachmentImageRefresh());

        // Block Cursor Extension - DISABLED to prevent layout shift
        // Using native red caret instead

        const TableCellWithBackground = TableCell.extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    backgroundColor: {
                        default: null,
                        parseHTML: (element) =>
                            element.getAttribute('data-background-color') || element.style.backgroundColor || null,
                        renderHTML: (attributes) => {
                            const bg = attributes['backgroundColor'];
                            if (!bg) {
                                return {};
                            }
                            return {
                                'data-background-color': bg,
                                style: `background-color: ${bg}`,
                            };
                        },
                    },
                };
            },
        });

        const TableHeaderWithBackground = TableHeader.extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    backgroundColor: {
                        default: null,
                        parseHTML: (element) =>
                            element.getAttribute('data-background-color') || element.style.backgroundColor || null,
                        renderHTML: (attributes) => {
                            const bg = attributes['backgroundColor'];
                            if (!bg) {
                                return {};
                            }
                            return {
                                'data-background-color': bg,
                                style: `background-color: ${bg}`,
                            };
                        },
                    },
                };
            },
        });

        this.editor = new Editor({
            element: this.editorHost.nativeElement,
            content: this.content,
            editable: !this.readOnly,
            extensions: [
                StarterKit.configure({
                    bulletList: false,
                    orderedList: false,
                    listItem: false,
                    codeBlock: false,
                    gapcursor: false,
                    link: false,
                    underline: false,
                }),
                BulletListNoShortcut,
                OrderedListWithStart,
                ListItem,
                TaskListNoShortcut.configure({
                    HTMLAttributes: {
                        class: 'task-list',
                    },
                }),
                TaskItemCustom.configure({
                    nested: true,
                    HTMLAttributes: {
                        class: 'task-item',
                    },
                }),
                InlineTask,
                ParagraphIndent.configure({ types: ['paragraph'] }),
                Underline,
                TextStyle,
                Color.configure({ types: ['textStyle'] }),
                BackgroundColor.configure({ types: ['textStyle'] }),
                FontSize.configure({ types: ['textStyle'] }),
                FontFamily.configure({ types: ['textStyle'] }),
                Link.configure({ 
                    openOnClick: false, 
                    autolink: true, 
                    linkOnPaste: true,
                    HTMLAttributes: {
                        class: 'editor-link',
                        rel: 'noopener noreferrer',
                    },
                }),
                NoteLink.configure({
                    openOnClick: true,
                    onNavigate: (noteId: string) => this.navigateToNote(noteId),
                }),
                Phase1Image.configure({ inline: false, allowBase64: true }),
                ImageDrop.configure({ getHandlers: () => this.getImageDropHandlers() }),
                TextAlign.configure({ types: ['heading', 'paragraph'] }),
                Table.configure({ resizable: true }),
                TableTouchResize,
                TableColumnAutoFit,
                TableRow,
                TableHeaderWithBackground,
                TableCellWithBackground,
                Gapcursor,
                CollapsibleSection.configure({
                    onOpenMenu: (sectionPos: number, triggerElement: HTMLElement, event?: Event) => {
                        this.ngZone.run(() => {
                            this.openCollapsibleSectionMenu(sectionPos, triggerElement, event?.type === 'mousedown');
                        });
                    },
                }),
                CollapsibleTitle,
                CollapsibleContent,
                KeyboardShortcuts,
                CodeBlockWithCopy,
                BlockTypography,
            ],
            onCreate: ({ editor }) => {
                this.lastKnownEditorHtml = editor.getHTML();
                this.syncToolbarState(editor);
                this.ensureTaskAssigneesLoaded(editor);
            },
            onUpdate: ({ editor, transaction }) => {
                const htmlValue = editor.getHTML();
                this.lastKnownEditorHtml = htmlValue;
                this.contentChange.emit(htmlValue);
                
                const isCollapsibleToggle = transaction.getMeta('isCollapsibleToggle');
                const toggleSource = transaction.getMeta('toggleSource');
                const isCollapsibleStateRestore = transaction.getMeta('isCollapsibleStateRestore');
                // console.log('[Editor onUpdate] isCollapsibleToggle:', isCollapsibleToggle, 'source:', toggleSource);

                // Don't emit contentUpdated for collapsible section toggles
                // These are marked with metadata to prevent form updates
                // An attachment-id rewrite after sync is not a user edit: the host patches its own copy
                // (see attachmentIdReplaced$ in NoteEditBase) without marking the note changed.
                const isAttachmentIdRewrite = transaction.getMeta('isAttachmentIdRewrite');
                if (!isCollapsibleToggle && !isCollapsibleStateRestore && !isAttachmentIdRewrite) {
                    // console.log('[Editor onUpdate] Emitting contentUpdated');
                    this.contentUpdated.emit(htmlValue);
                } else {
                    // console.log('[Editor onUpdate] Skipping contentUpdated (collapsible toggle)');
                }

                if (isCollapsibleToggle) {
                    this.persistCollapsibleStateSnapshot();
                }

                // Update undo/redo button states
                this.canUndo = editor.can().undo();
                this.canRedo = editor.can().redo();
                this.syncToolbarState(editor);
                
                // Apply styling to legacy note links
                this.applyLegacyLinkStyling();
                this.scheduleAttachmentImageRefresh();
                this.ensureTaskAssigneesLoaded(editor);
            },
            onSelectionUpdate: ({ editor }) => {
                this.syncToolbarState(editor);
            }
        });

        // Initialize undo/redo button states
        this.canUndo = this.editor?.can().undo() || false;
        this.canRedo = this.editor?.can().redo() || false;

        // Listen for custom keyboard shortcut events
        this.noteLinkDialogHandler = () => {
            this.openNoteLinkDialog();
        };
        this.editorHost.nativeElement.addEventListener('openNoteLinkDialog', this.noteLinkDialogHandler);

        this.linkDialogHandler = () => {
            this.openLinkDialog();
        };
        this.editorHost.nativeElement.addEventListener('openLinkDialog', this.linkDialogHandler);

        this.saveNoteShortcutHandler = () => {
            this.saveShortcutTriggered.emit();
        };

        const host = this.editorHost.nativeElement;
        host.addEventListener('dragenter', this.onFileDragEnter);
        host.addEventListener('dragover', this.onFileDragOver);
        host.addEventListener('dragleave', this.onFileDragLeave);
        host.addEventListener('drop', this.onFileDropFallback);

        this.fileUploadService.attachmentIdReplaced$
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ oldId, newId }) => this.ngZone.run(() => this.rewriteAttachmentImageId(oldId, newId)));
        this.editorHost.nativeElement.addEventListener('saveNoteShortcut', this.saveNoteShortcutHandler);

        this.insertCollapsibleShortcutHandler = () => {
            this.insertCollapsibleSection();
        };
        this.editorHost.nativeElement.addEventListener('insertCollapsibleSectionFromSelection', this.insertCollapsibleShortcutHandler);

        this.inlineTaskEventHandler = (event: Event) => {
            const detail = (event as CustomEvent<InlineTaskEventDetail>).detail;
            if (detail) this.ngZone.run(() => this.onInlineTaskEvent(detail));
        };
        this.editorHost.nativeElement.addEventListener(INLINE_TASK_EVENT, this.inlineTaskEventHandler);

        this.editorClickHandler = (event: MouseEvent) => {
            this.handleEditorClick(event);
        };
        this.editorHost.nativeElement.addEventListener('click', this.editorClickHandler);

        // Apply styling to legacy note links to make them visible
        this.applyLegacyLinkStyling();

        this.ensureResizeIndicator();

        this.editorHost.nativeElement.addEventListener('mousemove', this.onEditorMouseMove);
        this.editorHost.nativeElement.addEventListener('mouseleave', this.onEditorMouseLeave);
        this.editorHost.nativeElement.addEventListener('mousedown', this.onEditorMouseDown);
        document.addEventListener('mousemove', this.onDocumentMouseMove);
        document.addEventListener('mouseup', this.onDocumentMouseUp);

        this.applyPersistedCollapsibleState('after-init');
        this.scheduleAttachmentImageRefresh();
        this.normalizeInlineFontSizesForZoom();

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.editor) return;

        if (changes['content']) {
            // The [content] binding round-trips through the parent on every
            // keystroke (onUpdate emits contentUpdated -> parent patches its form
            // -> the new value flows back in here as an @Input change). That echo
            // is NOT a new external content change — it's exactly what this editor
            // itself just produced — so it must never re-run setContent()/
            // applyPersistedCollapsibleState(). Doing so was the actual cause of
            // nested Collapsibles closing on the first keystroke typed into them:
            // re-parsing + re-applying "restored" open/closed state on every single
            // edit is fragile (whatever the exact trigger — a formatting mismatch
            // between the original HTML and TipTap's own re-serialization, a stale
            // persisted-state record, or something more subtle in how nested
            // same-type nodes re-parse) and is also simply unnecessary: the live
            // editor DOM is already correct, there is nothing to restore.
            //
            // Comparing directly against the HTML this instance itself last
            // produced (tracked in onUpdate/onCreate/below) sidesteps all of that:
            // if the incoming value is our own echo, skip entirely. Only content
            // that didn't originate from this editor (switching notes via the same
            // instance, remote sync/conflict resolution, "discard changes since
            // opening") reaches the reset-and-restore path below.
            if (this.content === this.lastKnownEditorHtml) {
                return;
            }

            const currentEditorHtml = this.editor.getHTML();
            // Normalize both by removing data-open attributes for comparison
            // This prevents collapsible state changes from triggering editor resets
            const normalizedInput = this.normalizeCollapsibleState(this.content);
            const normalizedEditor = this.normalizeCollapsibleState(currentEditorHtml);

            // console.log('[Editor ngOnChanges] Normalized comparison:',
            //             'equal:', normalizedInput === normalizedEditor,
            //             'inputLength:', normalizedInput.length,
            //             'editorLength:', normalizedEditor.length);

            if (normalizedInput !== normalizedEditor) {
                // console.log('[Editor ngOnChanges] Content mismatch, resetting editor');
                this.editor.commands.setContent(this.content, { emitUpdate: false });
                this.lastKnownEditorHtml = this.editor.getHTML();
                this.normalizeInlineFontSizesForZoom();
                this.applyPersistedCollapsibleState('content-changed');
                this.scheduleAttachmentImageRefresh();
            } else {
                // Byte-different from our last known HTML but semantically the same
                // once collapsible-only attributes are ignored (e.g. this editor's
                // own re-serialization settled on different attribute
                // ordering/whitespace than the original load). Nothing to reset,
                // but keep the tracker in sync with what's actually on screen so a
                // later genuine echo of *this* content is still recognized as such.
                this.lastKnownEditorHtml = this.content;
            }
        }

        if (changes['noteId'] && !changes['noteId'].firstChange) {
            this.applyPersistedCollapsibleState('note-changed');
            this.scheduleAttachmentImageRefresh();
        }

        if (changes['readOnly']) {
            this.editor.setEditable(!this.readOnly);
            if (this.readOnly) {
                this.closeImageNodeMenu();
            }
            refreshInlineTaskViews(this.editor);
        }

        if (changes['spaceId'] && !changes['spaceId'].firstChange && this.taskAssigneesRequested) {
            // Owners must be members of the note's Space: reload for the new one.
            this.taskAssigneesRequested = false;
            this.ensureTaskAssigneesLoaded(this.editor);
        }
    }
    
    private normalizeCollapsibleState(html: string): string {
        if (!html) return '';
        // Remove local-only collapsible attributes to avoid content reset loops.
        return html
            .replace(/\s*data-open="(?:true|false)"/g, '')
            .replace(/\s*data-collapsible-id="[^"]*"/g, '');
    }

    private createCollapsibleId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `col-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    private getNoteStorageKey(): string | null {
        const trimmedNoteId = (this.noteId ?? '').trim();
        if (!trimmedNoteId) {
            return null;
        }

        return `note:${trimmedNoteId}`;
    }

    private getPersistedCollapsibleState(): PersistedCollapsibleState {
        if (typeof localStorage === 'undefined') {
            return { notes: {} };
        }

        const raw = localStorage.getItem(COLLAPSIBLE_STATE_STORAGE_KEY);
        if (!raw) {
            return { notes: {} };
        }

        try {
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object' || typeof parsed.notes !== 'object' || parsed.notes === null) {
                return { notes: {} };
            }

            return parsed as PersistedCollapsibleState;
        } catch {
            return { notes: {} };
        }
    }

    private writePersistedCollapsibleState(state: PersistedCollapsibleState): void {
        if (typeof localStorage === 'undefined') {
            return;
        }

        const pruned = this.prunePersistedNotes(state);
        localStorage.setItem(COLLAPSIBLE_STATE_STORAGE_KEY, JSON.stringify(pruned));
    }

    private prunePersistedNotes(state: PersistedCollapsibleState): PersistedCollapsibleState {
        const entries = Object.entries(state.notes || {});
        if (entries.length <= COLLAPSIBLE_NOTE_RETENTION_LIMIT) {
            return state;
        }

        entries.sort(([, a], [, b]) => {
            const aTime = Date.parse(a?.lastAccessed || '') || 0;
            const bTime = Date.parse(b?.lastAccessed || '') || 0;
            return bTime - aTime;
        });

        const kept = entries.slice(0, COLLAPSIBLE_NOTE_RETENTION_LIMIT);
        return {
            notes: Object.fromEntries(kept),
        };
    }

    private hashString(input: string): string {
        let hash = 5381;
        for (let i = 0; i < input.length; i += 1) {
            hash = ((hash << 5) + hash) + input.charCodeAt(i);
            hash |= 0;
        }
        return Math.abs(hash).toString(36);
    }

    private getCollapsibleElementId(node: ProseMirrorNode, fallbackCountBySignature: Map<string, number>): string {
        const explicitId = typeof node.attrs['collapsibleId'] === 'string' ? node.attrs['collapsibleId'].trim() : '';
        const prefix = this.editorId ? `${this.editorId}:` : '';
        if (explicitId) {
            return `${prefix}${explicitId}`;
        }

        const titleNode = node.childCount > 0 ? node.child(0) : null;
        const contentNode = node.childCount > 1 ? node.child(1) : null;
        const titleText = titleNode?.textContent?.trim() || '';
        const contentText = contentNode?.textContent?.trim() || '';
        const signature = this.hashString(`${titleText}|${contentText.slice(0, 200)}|${contentText.length}`);
        const currentCount = fallbackCountBySignature.get(signature) ?? 0;
        const nextCount = currentCount + 1;
        fallbackCountBySignature.set(signature, nextCount);
        return `${prefix}fallback:${signature}:${nextCount}`;
    }

    private collectCollapsibleElementsFromDoc(): Record<string, PersistedCollapsibleElementState> {
        if (!this.editor) {
            return {};
        }

        const nowIso = new Date().toISOString();
        const elements: Record<string, PersistedCollapsibleElementState> = {};
        const fallbackCountBySignature = new Map<string, number>();

        this.editor.state.doc.descendants((node) => {
            if (node.type.name !== 'collapsibleSection') {
                return true;
            }

            const elementId = this.getCollapsibleElementId(node, fallbackCountBySignature);
            elements[elementId] = {
                expanded: !!node.attrs['open'],
                updatedAt: nowIso,
            };

            return true;
        });

        return elements;
    }

    private persistCollapsibleStateSnapshot(): void {
        const noteStorageKey = this.getNoteStorageKey();
        if (!noteStorageKey || !this.editor) {
            return;
        }

        const elements = this.collectCollapsibleElementsFromDoc();
        const existingState = this.getPersistedCollapsibleState();

        existingState.notes[noteStorageKey] = {
            lastAccessed: new Date().toISOString(),
            elements,
        };

        this.writePersistedCollapsibleState(existingState);
    }

    private applyPersistedCollapsibleState(reason: string): void {
        if (!this.editor) {
            return;
        }

        const noteStorageKey = this.getNoteStorageKey();
        if (!noteStorageKey) {
            return;
        }

        const persistedState = this.getPersistedCollapsibleState();
        const noteState = persistedState.notes[noteStorageKey];
        const tr = this.editor.state.tr;
        tr.setMeta('isCollapsibleToggle', true);
        tr.setMeta('isCollapsibleStateRestore', true);
        tr.setMeta('toggleSource', `collapsible-state-restore:${reason}`);

        let modified = false;
        const fallbackCountBySignature = new Map<string, number>();

        this.editor.state.doc.descendants((node, pos) => {
            if (node.type.name !== 'collapsibleSection') {
                return true;
            }

            const elementId = this.getCollapsibleElementId(node, fallbackCountBySignature);
            const persistedElement = noteState?.elements?.[elementId];
            // No saved record yet (freshly inserted/typed-into section, or a note
            // opened for the first time in this browser) — leave the section's
            // current open state alone rather than defaulting it closed. Forcing
            // it closed here is what caused sections (nested ones especially,
            // since they're less likely to already have an explicit toggle
            // history) to visibly collapse the moment the user typed into them:
            // this function is a *restore*, and with nothing to restore it must
            // not override live state.
            const shouldBeExpanded = persistedElement ? !!persistedElement.expanded : !!node.attrs['open'];

            if (!!node.attrs['open'] !== shouldBeExpanded) {
                tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    open: shouldBeExpanded,
                });
                modified = true;
            }

            return true;
        });

        persistedState.notes[noteStorageKey] = {
            lastAccessed: new Date().toISOString(),
            elements: noteState?.elements ?? {},
        };
        this.writePersistedCollapsibleState(persistedState);

        if (modified) {
            this.editor.view.dispatch(tr);
        }
    }

    ngOnDestroy(): void {
        this.updateStyleTransferCursorState(false);
        this.closeImageNodeMenu();
        this.clearTableResizeCue();
        this.destroyResizeIndicator();
        this.editor?.destroy();
        this.revokeAttachmentImageObjectUrls();
        if (this.noteLinkDialogHandler) {
            this.editorHost.nativeElement.removeEventListener('openNoteLinkDialog', this.noteLinkDialogHandler);
        }
        if (this.linkDialogHandler) {
            this.editorHost.nativeElement.removeEventListener('openLinkDialog', this.linkDialogHandler);
        }
        if (this.saveNoteShortcutHandler) {
            this.editorHost.nativeElement.removeEventListener('saveNoteShortcut', this.saveNoteShortcutHandler);
        }
        if (this.insertCollapsibleShortcutHandler) {
            this.editorHost.nativeElement.removeEventListener('insertCollapsibleSectionFromSelection', this.insertCollapsibleShortcutHandler);
        }
        if (this.editorClickHandler) {
            this.editorHost.nativeElement.removeEventListener('click', this.editorClickHandler);
        }
        if (this.inlineTaskEventHandler) {
            this.editorHost.nativeElement.removeEventListener(INLINE_TASK_EVENT, this.inlineTaskEventHandler);
        }
        this.taskAssigneeSubscription?.unsubscribe();
        this.editorHost.nativeElement.removeEventListener('dragenter', this.onFileDragEnter);
        this.editorHost.nativeElement.removeEventListener('dragover', this.onFileDragOver);
        this.editorHost.nativeElement.removeEventListener('dragleave', this.onFileDragLeave);
        this.editorHost.nativeElement.removeEventListener('drop', this.onFileDropFallback);
        this.editorHost.nativeElement.removeEventListener('mousemove', this.onEditorMouseMove);
        this.editorHost.nativeElement.removeEventListener('mouseleave', this.onEditorMouseLeave);
        this.editorHost.nativeElement.removeEventListener('mousedown', this.onEditorMouseDown);
        document.removeEventListener('mousemove', this.onDocumentMouseMove);
        document.removeEventListener('mouseup', this.onDocumentMouseUp);
        if (this.styleTransferSingleClickTimeoutId !== null) {
            window.clearTimeout(this.styleTransferSingleClickTimeoutId);
            this.styleTransferSingleClickTimeoutId = null;
        }
        if (this.styleTransferApplyTimeoutId !== null) {
            window.clearTimeout(this.styleTransferApplyTimeoutId);
            this.styleTransferApplyTimeoutId = null;
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private handleTableResizeMouseDown(event: MouseEvent): void {
        if (!this.editor?.isEditable || this.readOnly) {
            return;
        }

        const target = event.target as HTMLElement | null;
        const handle = target?.closest('.column-resize-handle') as HTMLElement | null;
        if (!handle) {
            return;
        }

        const cell = handle.closest('th,td') as HTMLElement | null;
        if (!cell) {
            return;
        }

        this.isTableResizeDragging = true;
        this.activeResizeEdge = 'right';
        this.applyTableResizeCue(cell, 'right');
        this.editorHost.nativeElement.classList.add('table-resize-dragging');
        document.body.classList.add('table-resize-dragging');
        this.positionResizeIndicator(cell, 'right', event.clientX, true);
    }

    private handleTableResizeMouseMove(event: MouseEvent): void {
        if (!this.isTableResizeDragging || !this.activeResizeCueCell) {
            return;
        }

        this.positionResizeIndicator(this.activeResizeCueCell, this.activeResizeEdge, event.clientX, true);
    }

    private handleTableResizeMouseUp(): void {
        if (!this.isTableResizeDragging) {
            return;
        }

        this.isTableResizeDragging = false;
        this.editorHost.nativeElement.classList.remove('table-resize-dragging');
        document.body.classList.remove('table-resize-dragging');

        if (this.resizeIndicatorEl) {
            this.resizeIndicatorEl.classList.remove('dragging');
        }
    }

    private updateTableResizeCue(event: MouseEvent): void {
        if (!this.editor?.isEditable || this.readOnly) {
            this.clearTableResizeCue();
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            this.clearTableResizeCue();
            return;
        }

        const handle = target.closest('.column-resize-handle') as HTMLElement | null;
        if (handle) {
            const handleCell = handle.closest('th,td') as HTMLElement | null;
            this.applyTableResizeCue(handleCell, 'right');
            return;
        }

        const cell = target.closest('th,td') as HTMLElement | null;
        if (!cell || !this.editorHost.nativeElement.contains(cell)) {
            this.clearTableResizeCue();
            return;
        }

        const rect = cell.getBoundingClientRect();
        const nearRightEdge = Math.abs(rect.right - event.clientX) <= this.resizeEdgeThresholdPx;
        const nearLeftEdge = Math.abs(event.clientX - rect.left) <= this.resizeEdgeThresholdPx;

        if (!nearRightEdge && !nearLeftEdge) {
            this.clearTableResizeCue();
            return;
        }

        this.applyTableResizeCue(cell, nearRightEdge ? 'right' : 'left');
    }

    private applyTableResizeCue(cell: HTMLElement | null, edge: 'left' | 'right'): void {
        if (!cell) {
            this.clearTableResizeCue();
            return;
        }

        if (this.activeResizeCueCell && this.activeResizeCueCell !== cell) {
            this.activeResizeCueCell.classList.remove(
                'table-resize-candidate-cell',
                'table-resize-dragging-cell',
                'table-resize-edge-left',
                'table-resize-edge-right',
            );
        }

        this.activeResizeCueCell = cell;
        this.activeResizeEdge = edge;
        cell.classList.add('table-resize-candidate-cell', edge === 'right' ? 'table-resize-edge-right' : 'table-resize-edge-left');
        if (this.isTableResizeDragging) {
            cell.classList.add('table-resize-dragging-cell');
        } else {
            cell.classList.remove('table-resize-dragging-cell');
        }
        this.editorHost.nativeElement.classList.add('table-resize-candidate');
        document.body.classList.add('table-resize-candidate');
        this.positionResizeIndicator(cell, edge, undefined, this.isTableResizeDragging);
    }

    private clearTableResizeCue(): void {
        if (this.isTableResizeDragging) {
            return;
        }

        if (this.activeResizeCueCell) {
            this.activeResizeCueCell.classList.remove(
                'table-resize-candidate-cell',
                'table-resize-dragging-cell',
                'table-resize-edge-left',
                'table-resize-edge-right',
            );
            this.activeResizeCueCell = null;
        }

        this.editorHost.nativeElement.classList.remove('table-resize-candidate');
        document.body.classList.remove('table-resize-candidate');
        if (this.resizeIndicatorEl) {
            this.resizeIndicatorEl.classList.remove('visible', 'edge-left', 'edge-right', 'dragging');
        }
    }

    private ensureResizeIndicator(): void {
        if (typeof document === 'undefined' || this.resizeIndicatorEl) {
            return;
        }

        const el = document.createElement('div');
        el.className = 'table-resize-hover-indicator';
        document.body.appendChild(el);
        this.resizeIndicatorEl = el;
    }

    private destroyResizeIndicator(): void {
        if (this.resizeIndicatorEl?.parentNode) {
            this.resizeIndicatorEl.parentNode.removeChild(this.resizeIndicatorEl);
        }
        this.resizeIndicatorEl = null;
    }

    private positionResizeIndicator(
        cell: HTMLElement,
        edge: 'left' | 'right',
        pointerX?: number,
        isDragging = false,
    ): void {
        this.ensureResizeIndicator();
        if (!this.resizeIndicatorEl) {
            return;
        }

        const cellRect = cell.getBoundingClientRect();
        const tableRect = cell.closest('table')?.getBoundingClientRect() ?? cellRect;
        const indicatorWidth = isDragging ? 12 : 8;
        const x = typeof pointerX === 'number'
            ? pointerX
            : (edge === 'right' ? cellRect.right : cellRect.left);
        const left = x - indicatorWidth / 2;

        this.resizeIndicatorEl.style.left = `${Math.round(left)}px`;
        this.resizeIndicatorEl.style.top = `${Math.round(tableRect.top - 2)}px`;
        this.resizeIndicatorEl.style.height = `${Math.round(tableRect.height + 4)}px`;
        this.resizeIndicatorEl.style.width = `${indicatorWidth}px`;
        this.resizeIndicatorEl.classList.add('visible', edge === 'right' ? 'edge-right' : 'edge-left');
        if (isDragging) {
            this.resizeIndicatorEl.classList.add('dragging');
        } else {
            this.resizeIndicatorEl.classList.remove('dragging');
        }
        this.resizeIndicatorEl.classList.remove(edge === 'right' ? 'edge-left' : 'edge-right');
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        this.isTableMenuOpen = false;

        const target = event.target as HTMLElement | null;
        this.cancelStyleTransferForToolbarInteraction(target);
        if (target?.closest('.image-node-menu-trigger') || target?.closest('.image-node-menu-panel')) {
            return;
        }

        if (target?.closest('.image-node-selected')) {
            return;
        }

        if (target?.closest('.collapsible-section-menu-panel') || target?.closest('.collapsible-menu-btn')) {
            return;
        }

        if (this.collapsibleMenuOpeningPress) {
            this.collapsibleMenuOpeningPress = false;
            return;
        }

        this.closeCollapsibleSectionMenu();
        this.closeImageNodeMenu();
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.closeCollapsibleSectionMenu();
        this.closeImageNodeMenu();
        this.cancelStyleTransferMode();
    }

    @HostListener('document:mouseup', ['$event'])
    onDocumentMouseUpForStyleTransfer(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;

        if (this.styleTransferApplyTimeoutId !== null) {
            window.clearTimeout(this.styleTransferApplyTimeoutId);
        }

        // Defer until after ProseMirror updates selection for this mouse action.
        this.styleTransferApplyTimeoutId = window.setTimeout(() => {
            this.styleTransferApplyTimeoutId = null;
            this.tryApplyStyleTransferOnSelectionEnd(target);
        }, 24);
    }

    get isInTable(): boolean {
        return !!this.editor?.isActive('table');
    }

    btn(active: boolean | undefined): string {
        return active
            ? 'bg-primary-100 text-primary-700 border-primary-300'
            : '';
    }

    toolbarFieldClass(active: boolean): string {
        return active
            ? 'border-primary-300 bg-primary-50 text-primary-700 ring-1 ring-primary-200'
            : '';
    }

    getStyleTransferIcon(): string {
        return 'format_paint';
    }

    getStyleTransferTitle(): string {
        if (this.styleTransferMode === 'paste') {
            return this.isStyleTransferSticky
                ? 'Format Paint (locked). Select text to apply. Press Esc to exit.'
                : 'Format Paint. Select text to apply once.';
        }

        return 'Format Paint (Copy style)';
    }

    onStyleTransferButtonClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.styleTransferSingleClickTimeoutId !== null) {
            window.clearTimeout(this.styleTransferSingleClickTimeoutId);
        }

        this.styleTransferSingleClickTimeoutId = window.setTimeout(() => {
            this.styleTransferSingleClickTimeoutId = null;
            this.handleStyleTransferSingleClick();
        }, 225);
    }

    onStyleTransferButtonDoubleClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.styleTransferSingleClickTimeoutId !== null) {
            window.clearTimeout(this.styleTransferSingleClickTimeoutId);
            this.styleTransferSingleClickTimeoutId = null;
        }

        const didCopy = this.captureCurrentStyleToBuffer();
        if (!didCopy) {
            return;
        }

        this.setStyleTransferPasteMode(true);
    }

    private handleStyleTransferSingleClick(): void {
        if (this.styleTransferMode === 'paste') {
            this.cancelStyleTransferMode();
            return;
        }

        const didCopy = this.captureCurrentStyleToBuffer();
        if (!didCopy) {
            return;
        }

        this.setStyleTransferPasteMode(false);
    }

    private setStyleTransferPasteMode(sticky: boolean): void {
        this.styleTransferMode = 'paste';
        this.isStyleTransferSticky = sticky;
        this.styleTransferSelectionArmed = true;
        this.styleTransferLastAppliedSelectionKey = null;
        const selection = this.editor?.state.selection;
        this.styleTransferActivationSelectionKey = selection
            ? this.getSelectionKey(selection.from, selection.to)
            : null;
        this.updateStyleTransferCursorState(true);
    }

    private updateStyleTransferCursorState(active: boolean): void {
        this.editorHost?.nativeElement.classList.toggle('style-transfer-cursor-active', active);

        const editorDom = this.editor?.view?.dom as HTMLElement | undefined;
        editorDom?.classList.toggle('style-transfer-cursor-active', active);
    }

    private captureCurrentStyleToBuffer(): boolean {
        const editor = this.editor;
        if (!editor) {
            return false;
        }

        const textStyleAttributes = this.resolveTextStyleAttributes(editor);
        const activeFontSize = this.getActiveFontSize(editor, textStyleAttributes['fontSize']);
        const fontSizePx = activeFontSize ? this.extractFontSizePx(activeFontSize) : null;

        let headingLevel: 1 | 2 | 3 | 4 | 5 | 6 | null = null;
        for (const level of [1, 2, 3, 4, 5, 6] as const) {
            if (editor.isActive('heading', { level })) {
                headingLevel = level;
                break;
            }
        }

        let listType: CopiedStyleListType = null;
        if (editor.isActive('taskList')) {
            listType = 'task';
        } else if (editor.isActive('orderedList')) {
            listType = 'ordered';
        } else if (editor.isActive('bulletList')) {
            listType = 'bullet';
        }

        const orderedStartRaw = editor.getAttributes('orderedList')?.['start'];
        const orderedListStart = typeof orderedStartRaw === 'number' ? orderedStartRaw : null;

        this.copiedStyleBuffer = {
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            strike: editor.isActive('strike'),
            textColor: this.normalizeToolbarColor(textStyleAttributes['color']),
            backgroundColor: this.normalizeToolbarColor(textStyleAttributes['backgroundColor']),
            fontSizePx,
            headingLevel,
            listType,
            orderedListStart,
            isCodeBlock: editor.isActive('codeBlock'),
            textAlign: this.getCurrentTextAlign(editor),
        };

        const selection = editor.state.selection;
        this.styleTransferSourceSelectionKey = this.getSelectionKey(selection.from, selection.to);
        this.styleTransferSourceRange = { from: selection.from, to: selection.to };

        return true;
    }

    private resolveTextStyleAttributes(editor: Editor): Record<string, unknown> {
        const { state } = editor;
        const selection = state.selection;

        if (!selection.empty) {
            const fromRange = this.findTextStyleAttrsInRange(editor, selection.from, selection.to);
            if (this.hasAnyTextStyleAttribute(fromRange)) {
                return fromRange;
            }
        }

        const fromActive = editor.getAttributes('textStyle') ?? {};
        if (this.hasAnyTextStyleAttribute(fromActive)) {
            return fromActive;
        }

        const fromStoredOrCursor = this.extractTextStyleAttrsFromMarks(
            state.storedMarks ?? selection.$from.marks()
        );
        if (this.hasAnyTextStyleAttribute(fromStoredOrCursor)) {
            return fromStoredOrCursor;
        }

        if (selection.empty) {
            const fromBlock = this.findTextStyleAttrsInCurrentBlock(selection.$from.parent);
            if (this.hasAnyTextStyleAttribute(fromBlock)) {
                return fromBlock;
            }
        }

        return fromActive;
    }

    private hasAnyTextStyleAttribute(attrs: Record<string, unknown>): boolean {
        return !!(
            this.normalizeToolbarColor(attrs['color']) ||
            this.normalizeToolbarColor(attrs['backgroundColor']) ||
            (typeof attrs['fontSize'] === 'string' && attrs['fontSize'].trim())
        );
    }

    private extractTextStyleAttrsFromMarks(marks: readonly any[] | null | undefined): Record<string, unknown> {
        if (!marks || marks.length === 0) {
            return {};
        }

        const textStyleMark = marks.find((mark: any) => mark?.type?.name === 'textStyle');
        return textStyleMark?.attrs ?? {};
    }

    private findTextStyleAttrsInRange(editor: Editor, from: number, to: number): Record<string, unknown> {
        let found: Record<string, unknown> = {};

        editor.state.doc.nodesBetween(from, to, (node) => {
            if (Object.keys(found).length > 0) {
                return false;
            }

            if (!node.isText || !node.marks || node.marks.length === 0) {
                return true;
            }

            const attrs = this.extractTextStyleAttrsFromMarks(node.marks as any[]);
            if (this.hasAnyTextStyleAttribute(attrs)) {
                found = attrs;
                return false;
            }

            return true;
        });

        return found;
    }

    private findTextStyleAttrsInCurrentBlock(blockNode: ProseMirrorNode): Record<string, unknown> {
        let found: Record<string, unknown> = {};

        blockNode.descendants((node) => {
            if (Object.keys(found).length > 0) {
                return false;
            }

            if (!node.isText || !node.marks || node.marks.length === 0) {
                return true;
            }

            const attrs = this.extractTextStyleAttrsFromMarks(node.marks as any[]);
            if (this.hasAnyTextStyleAttribute(attrs)) {
                found = attrs;
                return false;
            }

            return true;
        });

        return found;
    }

    private applyCopiedStyleFromBuffer(): boolean {
        const editor = this.editor;
        const style = this.copiedStyleBuffer;
        if (!editor || !style) {
            return false;
        }

        const selection = editor.state.selection;
        if (!(selection instanceof TextSelection) && !selection.empty) {
            return false;
        }

        const constrainedRange = this.getStyleTransferTargetRange(editor.state, selection.from, selection.to);
        const initialFrom = constrainedRange.from;
        const initialTo = constrainedRange.to;

        // Apply heading/alignment to the active block context first.
        if (style.headingLevel) {
            editor.chain().focus().setHeading({ level: style.headingLevel }).run();
        }

        if (!style.isCodeBlock && style.textAlign) {
            editor.chain().focus().setTextAlign(style.textAlign).run();
        }

        const chain = editor.chain().focus().setTextSelection({ from: initialFrom, to: initialTo });
        if (style.bold) {
            chain.setBold();
        }
        if (style.italic) {
            chain.setItalic();
        }
        if (style.underline) {
            chain.setUnderline();
        }
        if (style.strike) {
            chain.setStrike();
        }

        if (style.fontSizePx !== null) {
            const normalizedPx = `${style.fontSizePx}px`;
            chain.setFontSize(this.toZoomAwareFontSize(normalizedPx));
        }

        // Apply color after font-size changes.
        if (style.textColor) {
            chain.setColor(style.textColor);
        }

        if (style.backgroundColor) {
            chain.setBackgroundColor(style.backgroundColor);
        }

        // In list items, command-based mark updates can be skipped on some mixed
        // selections. Force-apply textStyle attrs in the same transaction so undo
        // treats paste-format as one logical step.
        if (!selection.empty) {
            if (style.textColor) {
                chain.command(({ tr, state }) =>
                    this.addTextStyleAttrToTransaction(tr, state, initialFrom, initialTo, 'color', style.textColor!)
                );
            }

            if (style.backgroundColor) {
                chain.command(({ tr, state }) =>
                    this.addTextStyleAttrToTransaction(tr, state, initialFrom, initialTo, 'backgroundColor', style.backgroundColor!)
                );
            }

            if (style.fontSizePx !== null) {
                const zoomAware = this.toZoomAwareFontSize(`${style.fontSizePx}px`);
                chain.command(({ tr, state }) =>
                    this.addTextStyleAttrToTransaction(tr, state, initialFrom, initialTo, 'fontSize', zoomAware)
                );
            }
        }

        chain.run();

        return true;
    }

    private getStyleTransferTargetRange(state: any, from: number, to: number): { from: number; to: number } {
        if (from >= to) {
            return { from, to };
        }

        const listFrom = this.resolveNearestListItemBoundary(state, from);
        const listTo = this.resolveNearestListItemBoundary(state, Math.max(from, to - 1));

        if (!listFrom || !listTo) {
            return { from, to };
        }

        // Keep list structure boundaries intact but allow formatting to span
        // all selected list items, not just the first one.
        const clampedFrom = Math.max(from, listFrom.contentFrom);
        const clampedTo = Math.min(to, listTo.contentTo);
        return clampedTo > clampedFrom
            ? { from: clampedFrom, to: clampedTo }
            : { from, to };
    }

    private resolveNearestListItemBoundary(
        state: any,
        pos: number
    ): { pos: number; contentFrom: number; contentTo: number } | null {
        const $pos = state.doc.resolve(pos);

        for (let depth = $pos.depth; depth > 0; depth -= 1) {
            const node = $pos.node(depth);
            const nodeName = node.type.name;
            if (nodeName !== 'listItem' && nodeName !== 'taskItem') {
                continue;
            }

            const nodePos = $pos.before(depth);
            return {
                pos: nodePos,
                contentFrom: nodePos + 1,
                contentTo: nodePos + node.nodeSize - 1,
            };
        }

        return null;
    }

    private addTextStyleAttrToTransaction(
        tr: any,
        state: any,
        from: number,
        to: number,
        attrName: 'color' | 'backgroundColor' | 'fontSize',
        value: string
    ): boolean {
        if (from >= to) {
            return true;
        }

        const textStyleMarkType = state.schema.marks['textStyle'];
        if (!textStyleMarkType) {
            return true;
        }

        state.doc.nodesBetween(from, to, (node: any, pos: number) => {
            if (!node.isText) {
                return true;
            }

            const start = Math.max(from, pos);
            const end = Math.min(to, pos + node.nodeSize);
            if (start >= end) {
                return true;
            }

            const existingAttrs =
                node.marks.find((mark: any) => mark.type.name === 'textStyle')?.attrs ?? {};
            const nextAttrs = {
                ...existingAttrs,
                [attrName]: value,
            };

            tr = tr.addMark(start, end, textStyleMarkType.create(nextAttrs));
            return true;
        });

        return true;
    }

    private getCurrentTextAlign(editor: Editor): CopiedStyleAlign {
        const rawAlign =
            editor.getAttributes('heading')?.['textAlign'] ||
            editor.getAttributes('paragraph')?.['textAlign'];

        if (rawAlign === 'left' || rawAlign === 'center' || rawAlign === 'right' || rawAlign === 'justify') {
            return rawAlign;
        }

        return null;
    }

    private exitStyleTransferPasteMode(): void {
        this.styleTransferMode = 'copy';
        this.isStyleTransferSticky = false;
        this.styleTransferSelectionArmed = false;
        this.styleTransferLastAppliedSelectionKey = null;
        this.styleTransferSourceRange = null;
        this.styleTransferActivationSelectionKey = null;
        this.updateStyleTransferCursorState(false);
    }

    private cancelStyleTransferMode(): void {
        if (this.styleTransferMode !== 'paste') {
            return;
        }

        this.exitStyleTransferPasteMode();
    }

    private cancelStyleTransferForToolbarInteraction(target: HTMLElement | null): void {
        if (this.styleTransferMode !== 'paste' || !target) {
            return;
        }

        const toolbarElement = target.closest('.editor-toolbar-sticky');
        if (!toolbarElement) {
            return;
        }

        if (target.closest('.style-transfer-toggle')) {
            return;
        }

        const isToolbarControl = !!target.closest('button,select,app-color-swatch-picker');
        if (!isToolbarControl) {
            return;
        }

        this.cancelStyleTransferMode();
    }

    private tryApplyStyleTransferOnSelectionEnd(target: HTMLElement | null): void {
        const editor = this.editor;
        if (!editor || this.styleTransferMode !== 'paste' || !this.copiedStyleBuffer) {
            return;
        }

        if (!target) {
            return;
        }

        if (target.closest('.editor-toolbar-sticky')) {
            return;
        }

        const editorDom = editor.view.dom;
        if (!editorDom.contains(target)) {
            return;
        }

        const selection = editor.state.selection;
        const selectionKey = this.getSelectionKey(selection.from, selection.to);

        // Never apply format back onto the source selection.
        if (this.styleTransferSourceSelectionKey && selectionKey === this.styleTransferSourceSelectionKey) {
            return;
        }

        // Also block any overlap with the copied source range.
        if (this.styleTransferSourceRange && this.selectionOverlapsSourceRange(selection.from, selection.to)) {
            return;
        }

        // Ignore the selection active at the moment paste mode was enabled.
        if (this.styleTransferActivationSelectionKey && selectionKey === this.styleTransferActivationSelectionKey) {
            return;
        }

        if (!this.styleTransferSelectionArmed && selectionKey === this.styleTransferLastAppliedSelectionKey) {
            return;
        }

        if (!this.styleTransferSelectionArmed && this.styleTransferLastAppliedSelectionKey !== null) {
            return;
        }

        const applied = this.applyCopiedStyleFromBuffer();
        if (!applied) {
            return;
        }

        this.styleTransferLastAppliedSelectionKey = selectionKey;
        this.styleTransferSelectionArmed = false;

        if (!this.isStyleTransferSticky) {
            this.exitStyleTransferPasteMode();
        }
    }

    private getSelectionKey(from: number, to: number): string {
        return `${from}:${to}`;
    }

    private selectionOverlapsSourceRange(from: number, to: number): boolean {
        const source = this.styleTransferSourceRange;
        if (!source) {
            return false;
        }

        const currentStart = Math.min(from, to);
        const currentEnd = Math.max(from, to);
        const sourceStart = Math.min(source.from, source.to);
        const sourceEnd = Math.max(source.from, source.to);

        return currentStart < sourceEnd && sourceStart < currentEnd;
    }

    private syncToolbarState(editor: Editor): void {
        this.updateStyleTransferSelectionArming(editor);

        const textStyleAttributes = editor.getAttributes('textStyle') ?? {};
        this.currentTextColor = this.normalizeToolbarColor(textStyleAttributes['color']);
        this.currentBackgroundColor = this.normalizeToolbarColor(textStyleAttributes['backgroundColor']);
        this.currentFontFamily = typeof textStyleAttributes['fontFamily'] === 'string' ? textStyleAttributes['fontFamily'] : '';
        this.isBoldActive = editor.isActive('bold');
        this.isItalicActive = editor.isActive('italic');
        this.isUnderlineActive = editor.isActive('underline');
        this.isStrikeActive = editor.isActive('strike');
        this.isBulletListActive = editor.isActive('bulletList');
        this.isTaskListToolbarActive = editor.isActive('taskList');
        this.isOrderedListActive = editor.isActive('orderedList');
        this.isRestartOrderedListActive =
            this.isOrderedListActive && editor.getAttributes('orderedList')?.['start'] === 1;

        const activeFontSize = this.getActiveFontSize(editor, textStyleAttributes['fontSize']);
        const fontSizePx = activeFontSize ? this.extractFontSizePx(activeFontSize) : null;
        this.hasExplicitFontSize = fontSizePx !== null;
        this.currentFontSize = fontSizePx !== null ? String(fontSizePx) : '';
        this.currentHeadingLevel = this.getActiveHeadingLevel(editor);

        this.canUndo = editor.can().undo();
        this.canRedo = editor.can().redo();
        this.syncImageSelectionFromEditor(editor);
    }

    private updateStyleTransferSelectionArming(editor: Editor): void {
        if (this.styleTransferMode !== 'paste') {
            return;
        }

        const selection = editor.state.selection;
        if (selection.empty) {
            this.styleTransferSelectionArmed = true;
            return;
        }

        const selectionKey = this.getSelectionKey(selection.from, selection.to);
        if (this.styleTransferLastAppliedSelectionKey !== selectionKey) {
            this.styleTransferSelectionArmed = true;
        }
    }

    private normalizeToolbarColor(value: unknown): string | null {
        return typeof value === 'string' && value.trim() ? value.trim() : null;
    }

    private getActiveFontSize(editor: Editor, inlineFontSize: unknown): string | null {
        if (typeof inlineFontSize === 'string' && inlineFontSize.trim()) {
            return inlineFontSize;
        }

        const blockFontSize =
            editor.getAttributes('paragraph')?.['fontSize'] ||
            editor.getAttributes('heading')?.['fontSize'] ||
            editor.getAttributes('blockquote')?.['fontSize'] ||
            editor.getAttributes('listItem')?.['fontSize'];

        return typeof blockFontSize === 'string' && blockFontSize.trim() ? blockFontSize : null;
    }

    private getActiveHeadingLevel(editor: Editor): string {
        for (const level of [1, 2, 3, 4]) {
            if (editor.isActive('heading', { level })) {
                return String(level);
            }
        }

        return '';
    }

    private openCollapsibleSectionMenu(sectionPos: number, triggerElement: HTMLElement, openedByPress = false): void {
        this.closeImageNodeMenu();

        this.activeCollapsibleSectionPos = sectionPos;
        this.collapsibleMenuOpeningPress = openedByPress;
        if (openedByPress) {
            // The click ending this press is dispatched right after its mouseup — clear the guard after that.
            document.addEventListener('mouseup', () => setTimeout(() => (this.collapsibleMenuOpeningPress = false)), {
                once: true,
                capture: true,
            });
        }
        this.collapsibleSectionMenuPosition = this.collapsibleMenuPositionFor(triggerElement, 236);
        this.collapsibleSectionMenuVisible = true;

        // Re-place it with its real height once rendered (the edit menu is taller than the estimate),
        // so a menu opened near the bottom of the window never ends up covering its own button.
        this.cdr.detectChanges();
        const panel = this.elementRef.nativeElement.querySelector('.collapsible-section-menu-panel') as HTMLElement | null;
        if (panel && panel.offsetHeight > 0) {
            this.collapsibleSectionMenuPosition = this.collapsibleMenuPositionFor(triggerElement, panel.offsetHeight);
        }
    }

    /** Below the button if it fits, else above it, else wherever it fits best inside the viewport. */
    private collapsibleMenuPositionFor(triggerElement: HTMLElement, menuHeight: number): { top: number; left: number } {
        const rect = triggerElement.getBoundingClientRect();
        const menuWidth = 192;
        const margin = 12;
        const gap = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = rect.right - menuWidth;
        left = Math.min(Math.max(left, margin), viewportWidth - menuWidth - margin);

        const spaceBelow = viewportHeight - margin - (rect.bottom + gap);
        const spaceAbove = rect.top - gap - margin;
        let top: number;
        if (menuHeight <= spaceBelow) {
            top = rect.bottom + gap;
        } else if (menuHeight <= spaceAbove) {
            top = rect.top - gap - menuHeight;
        } else {
            // Fits neither way: keep it inside the viewport on the roomier side.
            top = spaceBelow >= spaceAbove ? rect.bottom + gap : margin;
            top = Math.max(margin, Math.min(top, viewportHeight - margin - menuHeight));
        }
        return { top, left };
    }

    closeCollapsibleSectionMenu(): void {
        this.collapsibleSectionMenuVisible = false;
    }

    private openImageNodeMenu(imagePos: number, imageElement: HTMLImageElement): void {
        this.closeCollapsibleSectionMenu();

        // A cropped image is larger than its visible part: place the menu by the crop frame.
        const rect = (imageElement.closest('.image-crop') ?? imageElement).getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = rect.right + 8;
        let top = rect.top + 8;

        if (left + 40 > viewportWidth - 12) {
            left = rect.left - 40;
        }

        if (left < 12) {
            left = 12;
        }

        if (top + 36 > viewportHeight - 12) {
            top = viewportHeight - 36 - 12;
        }

        if (top < 12) {
            top = 12;
        }

        this.activeImagePos = imagePos;
        this.setActiveImageElement(imageElement);
        this.imageNodeMenuPosition = { top, left };
        this.positionImageNodeMenuPanel();
        this.imageNodeMenuVisible = true;
        this.imageNodeMenuExpanded = false;
    }

    onImageNodeMenuTriggerClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.imageNodeMenuVisible) {
            return;
        }

        this.imageNodeMenuExpanded = !this.imageNodeMenuExpanded;
        if (this.imageNodeMenuExpanded) {
            this.positionImageNodeMenuPanel();
        }
    }

    private positionImageNodeMenuPanel(): void {
        const menuWidth = 192;
        const estimatedMenuHeight = 210;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = this.imageNodeMenuPosition.left + 40;
        let top = this.imageNodeMenuPosition.top;

        if (left + menuWidth > viewportWidth - 12) {
            left = this.imageNodeMenuPosition.left - menuWidth - 8;
        }

        if (left < 12) {
            left = 12;
        }

        if (top + estimatedMenuHeight > viewportHeight - 12) {
            top = viewportHeight - estimatedMenuHeight - 12;
        }

        if (top < 12) {
            top = 12;
        }

        this.imageNodeMenuPanelPosition = { top, left };
    }

    closeImageNodeMenu(): void {
        this.imageNodeMenuVisible = false;
        this.imageNodeMenuExpanded = false;
        this.activeImagePos = null;
        this.setActiveImageElement(null);
    }

    onImageNodeMenuInsertLineBefore(): void {
        this.imageNodeMenuExpanded = false;
        this.insertLineBeforeActiveImage();
        this.closeImageNodeMenu();
    }

    onImageNodeMenuProperties(): void {
        const activeImage = this.getActiveImageNode();
        this.imageNodeMenuExpanded = false;
        if (!activeImage) {
            this.closeImageNodeMenu();
            return;
        }

        this.openImageEditDialog(activeImage.node, activeImage.pos);
    }

    onImageNodeMenuCrop(): void {
        const activeImage = this.getActiveImageNode();
        this.imageNodeMenuExpanded = false;
        if (!activeImage || !this.editor) {
            this.closeImageNodeMenu();
            return;
        }

        // The image as the editor shows it (attachment images are shown from an authenticated blob URL).
        const dom = this.editor.view.nodeDOM(activeImage.pos);
        const img = dom instanceof HTMLImageElement ? dom : (dom instanceof HTMLElement ? dom.querySelector('img') : null);
        const src = img?.currentSrc || img?.getAttribute('src') || String(activeImage.node.attrs['src'] ?? '');
        if (!src) {
            this.closeImageNodeMenu();
            return;
        }
        this.ngZone.run(() => {
            this.imageCropPos = activeImage.pos;
            this.imageCropSrc = src;
            this.imageCropInitial = normalizeImageCrop(activeImage.node.attrs['crop']);
            this.imageCropDialogVisible = true;
        });
    }

    /** Applies a crop (or its removal). The image keeps its scale, as in Word: cutting off half of it
     * halves its displayed width, restoring it gives back the width it had. */
    onImageCropApplied(result: ImageCropResult): void {
        const pos = this.imageCropPos;
        this.imageCropPos = null;
        if (!this.editor || pos === null) return;
        const node = this.editor.state.doc.nodeAt(pos);
        if (!node || node.type.name !== 'image') return;

        const naturalWidth = result.naturalWidth ?? (Number(node.attrs['naturalWidth']) || null);
        const naturalHeight = result.naturalHeight ?? (Number(node.attrs['naturalHeight']) || null);
        const visible = (crop: unknown) => {
            const layout = imageCropLayout(crop, naturalWidth, naturalHeight);
            return layout ? { w: layout.visibleWidth, h: layout.visibleHeight } : { w: 1, h: 1 };
        };
        const before = visible(node.attrs['crop']);
        const after = visible(result.crop);
        /** A px size scaled by `factor`; other sizes (none, %) are kept as they are. */
        const scaled = (value: unknown, factor: number): unknown => {
            const match = /^\s*(\d+(?:\.\d+)?)(?:px)?\s*$/.exec(String(value ?? ''));
            return match ? `${Math.max(1, Math.round(Number(match[1]) * factor))}px` : value ?? null;
        };

        const attrs: Record<string, unknown> = {
            ...node.attrs,
            crop: result.crop,
            naturalWidth,
            naturalHeight,
            width: scaled(node.attrs['width'], after.w / before.w),
            height: node.attrs['keepAspectRatio'] === false ? scaled(node.attrs['height'], after.h / before.h) : node.attrs['height'],
        };
        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.setNodeMarkup(pos, undefined, attrs);
            dispatch?.(tr);
            return true;
        }).setNodeSelection(pos).run();
        this.closeImageNodeMenu();
    }

    onImageNodeMenuInsertLineAfter(): void {
        this.imageNodeMenuExpanded = false;
        this.insertLineAfterActiveImage();
        this.closeImageNodeMenu();
    }

    onImageNodeMenuDelete(): void {
        this.imageNodeMenuExpanded = false;
        this.deleteActiveImage();
        this.closeImageNodeMenu();
    }

    private syncImageSelectionFromEditor(editor: Editor): void {
        if (!editor.isEditable || this.readOnly) {
            this.closeImageNodeMenu();
            return;
        }

        const selection = editor.state.selection;
        if (!(selection instanceof NodeSelection) || selection.node.type.name !== 'image') {
            this.closeImageNodeMenu();
            return;
        }

        const imagePos = selection.from;
        const domNode = editor.view.nodeDOM(imagePos);
        const imageElement = domNode instanceof HTMLImageElement
            ? domNode
            : (domNode instanceof HTMLElement ? domNode.querySelector('img') : null);

        if (!imageElement) {
            this.closeImageNodeMenu();
            return;
        }

        this.openImageNodeMenu(imagePos, imageElement);
    }

    private setActiveImageElement(next: HTMLImageElement | null): void {
        if (this.activeImageElement && this.activeImageElement !== next) {
            this.activeImageElement.classList.remove('image-node-selected');
        }

        this.activeImageElement = next;
        if (this.activeImageElement) {
            this.activeImageElement.classList.add('image-node-selected');
        }
    }

    private getActiveImageNode(): { node: ProseMirrorNode; pos: number } | null {
        if (!this.editor || this.activeImagePos === null) {
            return null;
        }

        const pos = this.activeImagePos;
        const node = this.editor.state.doc.nodeAt(pos);
        if (!node || node.type.name !== 'image') {
            return null;
        }

        return { node, pos };
    }

    private insertLineBeforeActiveImage(): void {
        const activeImage = this.getActiveImageNode();
        if (!activeImage || !this.editor) {
            return;
        }

        const paragraphType = this.editor.state.schema.nodes['paragraph'];
        if (!paragraphType) {
            return;
        }

        const blankParagraph = paragraphType.create();
        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.insert(activeImage.pos, blankParagraph);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
    }

    private insertLineAfterActiveImage(): void {
        const activeImage = this.getActiveImageNode();
        if (!activeImage || !this.editor) {
            return;
        }

        const paragraphType = this.editor.state.schema.nodes['paragraph'];
        if (!paragraphType) {
            return;
        }

        const blankParagraph = paragraphType.create();
        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.insert(activeImage.pos + activeImage.node.nodeSize, blankParagraph);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
    }

    private deleteActiveImage(): void {
        const activeImage = this.getActiveImageNode();
        if (!activeImage || !this.editor) {
            return;
        }

        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.delete(activeImage.pos, activeImage.pos + activeImage.node.nodeSize);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
    }

    onCollapsibleSectionMenuCopy(): void {
        this.closeCollapsibleSectionMenu();
        void this.copyActiveCollapsibleSection();
    }

    onCollapsibleSectionMenuExportPdf(): void {
        this.closeCollapsibleSectionMenu();
        void this.exportActiveCollapsibleSectionAsPdf();
    }

    onCollapsibleSectionMenuDelete(): void {
        this.closeCollapsibleSectionMenu();
        this.deleteActiveCollapsibleSection();
    }

    onCollapsibleSectionMenuDuplicate(): void {
        this.closeCollapsibleSectionMenu();
        this.duplicateActiveCollapsibleSection();
    }

    onCollapsibleSectionMenuInsertLineBefore(): void {
        this.closeCollapsibleSectionMenu();
        this.insertLineBeforeActiveCollapsibleSection();
    }

    onCollapsibleSectionMenuInsertLineAfter(): void {
        this.closeCollapsibleSectionMenu();
        this.insertLineAfterActiveCollapsibleSection();
    }

    onCollapsibleSectionMenuClearContents(): void {
        this.closeCollapsibleSectionMenu();
        this.clearActiveCollapsibleSectionContents();
    }

    onCollapsibleSectionMenuCleanup(): void {
        this.closeCollapsibleSectionMenu();
        this.cleanupActiveCollapsibleSectionContents();
    }

    private getActiveCollapsibleSectionNode(): { node: ProseMirrorNode; pos: number } | null {
        if (!this.editor || this.activeCollapsibleSectionPos === null) {
            return null;
        }

        const pos = this.activeCollapsibleSectionPos;
        const node = this.editor.state.doc.nodeAt(pos);
        if (!node || node.type.name !== 'collapsibleSection') {
            return null;
        }

        return { node, pos };
    }

    private async copyActiveCollapsibleSection(): Promise<void> {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const serializer = DOMSerializer.fromSchema(this.editor.state.schema);
        const container = document.createElement('div');
        container.appendChild(serializer.serializeNode(activeSection.node));
        const html = container.innerHTML;
        const text = container.textContent || activeSection.node.textContent || '';

        try {
            if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
                const clipboardItem = new ClipboardItem({
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                    'text/html': new Blob([html], { type: 'text/html' }),
                });
                await navigator.clipboard.write([clipboardItem]);
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            }
        } catch (error) {
            console.error('Failed to copy collapsible section:', error);
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            }
        }
    }

    private async exportActiveCollapsibleSectionAsPdf(): Promise<void> {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const sectionNode = activeSection.node;
        const titleNode = sectionNode.childCount > 0 ? sectionNode.child(0) : null;
        const contentNode = sectionNode.childCount > 1 ? sectionNode.child(1) : null;
        const title = (titleNode?.textContent || '').trim() || 'Untitled section';

        const serializer = DOMSerializer.fromSchema(this.editor.state.schema);
        const contentContainer = document.createElement('div');

        if (contentNode) {
            contentNode.content.forEach((childNode) => {
                contentContainer.appendChild(serializer.serializeNode(childNode));
            });
        }

        const contentHtml = contentContainer.innerHTML || '<p></p>';

        try {
            await this.notePrintExportService.exportCollapsibleSectionToPdf(title, contentHtml);
        } catch (error) {
            console.error('Failed to export collapsible section to PDF:', error);
        }
    }

    private deleteActiveCollapsibleSection(): void {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const { pos, node } = activeSection;
        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.delete(pos, pos + node.nodeSize);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
        this.activeCollapsibleSectionPos = null;
    }

    private duplicateActiveCollapsibleSection(): void {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const { pos, node } = activeSection;
        const insertPos = pos + node.nodeSize;
        const duplicateNode = this.cloneWithFreshCollapsibleIds(node);

        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.insert(insertPos, duplicateNode);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();

        this.activeCollapsibleSectionPos = insertPos;
    }

    private cloneWithFreshCollapsibleIds(node: ProseMirrorNode): ProseMirrorNode {
        if (node.isText) {
            return this.editor!.state.schema.text(node.text || '', node.marks);
        }

        const childNodes: ProseMirrorNode[] = [];
        node.content.forEach((child) => {
            childNodes.push(this.cloneWithFreshCollapsibleIds(child));
        });

        const nextAttrs = { ...node.attrs } as Record<string, unknown>;
        if (node.type.name === 'collapsibleSection') {
            nextAttrs['collapsibleId'] = this.createCollapsibleId();
        }

        const nextContent = childNodes.length > 0 ? Fragment.fromArray(childNodes) : node.content;
        return node.type.create(nextAttrs, nextContent, node.marks);
    }

    private insertLineBeforeActiveCollapsibleSection(): void {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const { pos } = activeSection;
        const paragraphType = this.editor.state.schema.nodes['paragraph'];
        if (!paragraphType) {
            return;
        }

        const blankParagraph = paragraphType.create();
        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.insert(pos, blankParagraph);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
    }

    private insertLineAfterActiveCollapsibleSection(): void {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const { pos, node } = activeSection;
        const paragraphType = this.editor.state.schema.nodes['paragraph'];
        if (!paragraphType) {
            return;
        }

        const insertPos = pos + node.nodeSize;
        const blankParagraph = paragraphType.create();
        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.insert(insertPos, blankParagraph);
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
    }

    private clearActiveCollapsibleSectionContents(): void {
        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection || !this.editor) {
            return;
        }

        const { node, pos } = activeSection;
        const schema = this.editor.state.schema;
        const titleNode = node.childCount > 0 ? node.child(0) : null;
        const contentNodeType = schema.nodes['collapsibleContent'];
        const paragraphType = schema.nodes['paragraph'];

        if (!titleNode || !contentNodeType || !paragraphType) {
            return;
        }

        const clearedContentNode = contentNodeType.create(null, [paragraphType.create()]);
        const clearedSectionNode = node.type.create(
            { ...node.attrs, open: true },
            [titleNode, clearedContentNode],
            node.marks,
        );

        this.editor.chain().focus().command(({ tr, dispatch }) => {
            tr.replaceWith(pos, pos + node.nodeSize, clearedSectionNode);

            const sectionPos = tr.mapping.map(pos, -1);
            const contentPos = sectionPos + 1 + titleNode.nodeSize;
            const contentStart = contentPos + 1;
            const safePos = Math.min(contentStart, tr.doc.content.size);
            tr.setSelection(TextSelection.near(tr.doc.resolve(safePos), 1));

            if (dispatch) {
                dispatch(tr);
            }
            return true;
        }).run();
    }

    private cleanupActiveCollapsibleSectionContents(): void {
        const editor = this.editor;
        if (!editor) {
            return;
        }

        const currentSelection = editor.state.selection;
        if (!currentSelection.empty) {
            // Reuse existing cleanup implementation and keep user-selected scope.
            this.cleanupFormatting();
            return;
        }

        const activeSection = this.getActiveCollapsibleSectionNode();
        if (!activeSection) {
            return;
        }

        const { node, pos } = activeSection;
        if (node.childCount < 2) {
            return;
        }

        const titleNode = node.child(0);
        const contentNode = node.child(1);
        if (contentNode.type.name !== 'collapsibleContent') {
            return;
        }

        const contentPos = pos + 1 + titleNode.nodeSize;
        const contentStart = contentPos + 1;
        const contentEnd = contentStart + contentNode.content.size;

        if (contentEnd <= contentStart) {
            return;
        }

        editor.chain().focus().setTextSelection({ from: contentStart, to: contentEnd }).run();
        this.cleanupFormatting();
    }

    /**
     * Darkens a hex color by the specified percentage
     * @param hex Hex color value (e.g., '#ff0000')
     * @param percent Percentage to darken (0-100), where 90 means 90% darker
     */
    private darkenColor(hex: string, percent: number = 70): string {
        // Remove '#' if present
        hex = hex.replace('#', '');
        
        // Parse hex to RGB
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // Darken by reducing brightness (moving towards black)
        const darkFactor = (100 - percent) / 100;
        const darkR = Math.floor(r * darkFactor);
        const darkG = Math.floor(g * darkFactor);
        const darkB = Math.floor(b * darkFactor);
        
        // Convert back to hex
        const toHex = (n: number) => n.toString(16).padStart(2, '0');
        return `#${toHex(darkR)}${toHex(darkG)}${toHex(darkB)}`;
    }

    /**
     * Get the toolbar button style with darkened primary color
     */
    getToolbarButtonStyle(): any {
        const darkenedColor = this.darkenColor(this.toolbarIconColor, 20);
        return {
            color: darkenedColor
        };
    }

    private syncToolbarIconColor(): void {
        const styles = getComputedStyle(document.documentElement);
        const primaryThemeColor = styles.getPropertyValue('--p-primary-color').trim();
        const primaryUserColor = styles.getPropertyValue('--primary-color').trim();
        this.toolbarIconColor = primaryThemeColor || primaryUserColor || '#2563eb';
    }

    /**
     * Get the primary color from user settings for use in style bindings
     */
    getPrimaryColor(): string {
        const styles = getComputedStyle(document.documentElement);
        const primaryThemeColor = styles.getPropertyValue('--p-primary-color').trim();
        const primaryUserColor = styles.getPropertyValue('--primary-color').trim();
        return primaryThemeColor || primaryUserColor || '#3b82f6';
    }

    toggleBold(): void {
        this.editor?.chain().focus().toggleBold().run();
    }

    toggleAllCollapsibleSections(expandAll: boolean): void {
        if (!this.editor) return;

        const { doc } = this.editor.state;
        const tr = this.editor.state.tr;
        let modified = false;
        const positions: number[] = [];
        let needsSelectionMove = false;
        let selectionTargetPos: number | null = null;

        // Mark this transaction as a collapsible toggle
        tr.setMeta('isCollapsibleToggle', true);
        tr.setMeta('toggleSource', 'Collapse All button');

        doc.descendants((node, pos) => {
            if (node.type.name === 'collapsibleSection') {
                tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    open: expandAll,
                });
                positions.push(pos);
                modified = true;
                
                // Check if we need to move selection when collapsing
                if (!expandAll) {
                    const sectionEnd = pos + node.nodeSize;
                    if (this.editor!.state.selection.from >= pos && this.editor!.state.selection.from < sectionEnd) {
                        needsSelectionMove = true;
                        selectionTargetPos = sectionEnd;
                    }
                }
            }
            return true;
        });

        if (modified) {
            // Move selection out of collapsed sections if needed
            if (needsSelectionMove && selectionTargetPos !== null) {
                const resolvedPos = tr.doc.resolve(Math.min(selectionTargetPos, tr.doc.content.size - 1));
                tr.setSelection(TextSelection.near(resolvedPos));
                // console.log('[toggleAllCollapsibleSections] Moved selection out of collapsed section');
            }

            // console.log('[toggleAllCollapsibleSections] Setting', positions.length, 'sections to', expandAll ? 'expanded' : 'collapsed', 'at positions:', positions);
            this.editor.view.dispatch(tr);
        }
    }

    toggleItalic(): void {
        this.editor?.chain().focus().toggleItalic().run();
    }

    toggleUnderline(): void {
        this.editor?.chain().focus().toggleUnderline().run();
    }

    toggleStrike(): void {
        this.editor?.chain().focus().toggleStrike().run();
    }

    toggleBulletList(): void {
        const editor = this.editor;
        if (!editor) {
            return;
        }

        toggleBulletListSafely(editor);
    }

    toggleOrderedList(): void {
        const editor = this.editor;
        if (!editor) {
            return;
        }

        toggleOrderedListSafely(editor);
    }

    restartOrderedList(): void {
        const editor = this.editor;
        if (!editor) {
            return;
        }

        restartOrderedList(editor);
    }

    toggleChecklist(): void {
        const editor = this.editor;
        if (!editor) return;

        toggleTaskListSafely(editor);
    }

    // ── Inline tasks (tasks/frontend-inline-tasks-phase-1-requirements.md) ──────────────────────
    // The task rows live in the document (extensions/inline-task.ts); this component loads who can be
    // assigned, and owns the owner and due-date pickers the rows ask for.

    /** Insert a task at the cursor (toolbar "Add task", Ctrl/⌘+Alt+N). */
    insertTask(): void {
        if (!this.editor || this.readOnly) return;
        insertInlineTask(this.editor);
    }

    /** Puts the cursor at the end of a task's description and scrolls it into view, opening any
     * collapsed sections around it (Home Tasks view → click a task). False while the task isn't in
     * the document (yet). Already there (cursor at its end, nothing collapsed): nothing changes. */
    focusInlineTask(taskId: string): boolean {
        const editor = this.editor;
        if (!editor || editor.isDestroyed || !taskId) return false;
        const found = findInlineTask(editor.state.doc, taskId);
        if (!found) return false;

        const end = found.pos + found.node.nodeSize - 1;
        const tr = editor.state.tr;
        const $pos = editor.state.doc.resolve(found.pos);
        let opened = false;
        for (let depth = $pos.depth; depth > 0; depth--) {
            const node = $pos.node(depth);
            if (node.type.name === 'collapsibleSection' && !node.attrs['open']) {
                tr.setNodeMarkup($pos.before(depth), undefined, { ...node.attrs, open: true });
                opened = true;
            }
        }
        const { selection } = editor.state;
        if (!opened && selection.empty && selection.from === end && editor.view.hasFocus()) return true;

        if (opened) {
            // Opening a section isn't an edit of the note.
            tr.setMeta('isCollapsibleToggle', true);
            tr.setMeta('toggleSource', 'focus-inline-task');
        }
        tr.setSelection(TextSelection.create(tr.doc, end)).scrollIntoView();
        editor.view.dispatch(tr);
        editor.view.focus();
        const dom = editor.view.nodeDOM(found.pos);
        if (dom instanceof HTMLElement) dom.scrollIntoView({ block: 'center' });
        return true;
    }

    /** Members of the note's Space are loaded once the note has (or gets) a task. */
    private ensureTaskAssigneesLoaded(editor: Editor, force = false): void {
        if (this.taskAssigneesRequested || (!force && !hasInlineTasks(editor.state.doc))) return;
        this.taskAssigneesRequested = true;
        this.taskAssigneeSubscription?.unsubscribe();
        this.taskAssigneeSubscription = this.taskAssigneeService.assignees$(this.spaceId).subscribe((list) => {
            this.taskAssignees = list.assignees;
            this.taskAssigneeSource = list.source;
            if (this.taskOwnerDialogVisible) this.buildTaskOwnerOptions();
            if (this.editor && !this.editor.isDestroyed) {
                inlineTaskStorage(this.editor).assignees = list.assignees;
                refreshInlineTaskViews(this.editor);
            }
        });
    }

    /** Frontend task validation for a save: empty descriptions, invalid due dates, owners who lost
     * access. Problems are outlined in the editor; an empty list means the tasks are fine. */
    validateInlineTasks(): InlineTaskIssue[] {
        const editor = this.editor;
        if (!editor) return [];
        const tasks = allInlineTasks(editor.state.doc).map(({ node }) => ({
            id: String(node.attrs['id'] ?? ''),
            description: node.textContent,
            ownerId: node.attrs['ownerId'] ?? undefined,
            dueDate: node.attrs['dueDate'] ?? undefined,
        }));
        const issues = validateInlineTasks(tasks, this.taskAssignees);
        if (issues.length) revealInlineTaskErrors(editor);
        return issues;
    }

    private onInlineTaskEvent(detail: InlineTaskEventDetail): void {
        if (!this.editor || this.readOnly) return;
        if (detail.action === 'owner') this.openTaskOwnerPicker(detail.taskId, detail.trigger);
        else if (detail.action === 'due') this.openTaskDuePicker(detail.taskId);
        else if (detail.action === 'deleted') this.snackbarService.show('Task deleted. Use Undo to restore it.', 'info');
    }

    openTaskOwnerPicker(taskId: string, trigger: InlineTaskTrigger = 'click'): void {
        const found = this.editor ? findInlineTask(this.editor.state.doc, taskId) : null;
        if (!found) return;
        this.ensureTaskAssigneesLoaded(this.editor!, true);
        this.taskPickerTaskId = taskId;
        this.taskOwnerTrigger = trigger;
        this.taskOwnerApplied = false;
        this.taskOwnerCurrentId = found.node.attrs['ownerId'] ?? null;
        this.buildTaskOwnerOptions();
        this.taskOwnerDialogVisible = true;
    }

    private buildTaskOwnerOptions(): void {
        const current = this.taskOwnerCurrentId;
        const options: TaskOwnerOption[] = [{ id: null, name: 'No owner' }];
        for (const assignee of this.taskAssignees ?? []) options.push({ id: assignee.id, name: assignee.name });
        // An owner who lost access (or isn't in a list this device couldn't load) stays visible, marked.
        if (current && !options.some((o) => o.id === current)) {
            options.push({
                id: current,
                name: taskOwnerName(current, this.taskAssignees),
                ineligible: !isEligibleTaskOwner(current, this.taskAssignees),
            });
        }
        this.taskOwnerOptions = options;
        this.taskOwnerSelection = current;
    }

    get taskOwnerCurrentIneligible(): boolean {
        return !isEligibleTaskOwner(this.taskOwnerCurrentId, this.taskAssignees);
    }

    get taskOwnerListNote(): string | null {
        if (this.taskAssigneeSource === 'unavailable') return 'The member list for this note isn’t available offline. You can still clear the owner.';
        if (this.taskAssigneeSource === 'cache' || this.taskAssigneeSource === 'fallback') return 'Showing the last known members.';
        return null;
    }

    onTaskOwnerPicked(ownerId: string | null): void {
        const option = this.taskOwnerOptions.find((o) => o.id === ownerId);
        // Only eligible members (or no owner) can be picked; an ineligible current owner is shown, not offered.
        if (!option || option.ineligible || (ownerId && this.taskAssignees === null && ownerId !== this.taskOwnerCurrentId)) return;
        if (this.editor && this.taskPickerTaskId) {
            updateInlineTask(this.editor, this.taskPickerTaskId, { ownerId: ownerId ?? null });
        }
        this.taskOwnerApplied = true;
        this.taskOwnerDialogVisible = false;
    }

    onTaskOwnerDialogHide(): void {
        const editor = this.editor;
        if (!editor || editor.isDestroyed) return;
        // Typed `@` but picked nobody: it was just an @ (e.g. part of an email address).
        if (this.taskOwnerTrigger === '@' && !this.taskOwnerApplied) editor.chain().focus().insertContent('@').run();
        else editor.commands.focus();
        this.taskOwnerTrigger = 'click';
    }

    openTaskDuePicker(taskId: string): void {
        const found = this.editor ? findInlineTask(this.editor.state.doc, taskId) : null;
        if (!found) return;
        this.taskPickerTaskId = taskId;
        this.taskDueCurrent = found.node.attrs['dueDate'] ?? null;
        this.taskDueDraft = taskDueDateToDate(this.taskDueCurrent);
        this.taskDueDialogVisible = true;
    }

    /** Set (a picked Date, or 'today'/'tomorrow'/'next-week') or clear (null) the due date. */
    setTaskDueDate(value: Date | 'today' | 'tomorrow' | 'next-week' | null): void {
        const today = new Date();
        const date =
            value === 'today' ? today : value === 'tomorrow' ? addDays(today, 1) : value === 'next-week' ? addDays(today, 7) : value;
        const dueDate = date === null ? null : toTaskDueDate(date);
        if (date !== null && !dueDate) return; // not a valid date: keep the picker open
        if (this.editor && this.taskPickerTaskId) updateInlineTask(this.editor, this.taskPickerTaskId, { dueDate });
        this.taskDueDialogVisible = false;
    }

    onTaskDueDialogHide(): void {
        if (this.editor && !this.editor.isDestroyed) this.editor.commands.focus();
    }

    increaseIndent(): void {
        const editor = this.editor;
        if (!editor) return;

        indentAtCursor(editor);
    }

    decreaseIndent(): void {
        const editor = this.editor;
        if (!editor) return;

        outdentAtCursor(editor);
    }

    isTaskListActive(): boolean {
        return this.isTaskListToolbarActive;
    }

    toggleCodeBlock(): void {
        const editor = this.editor;
        if (!editor) return;

        const { from, to, empty } = editor.state.selection;

        if (empty) {
            editor.chain().focus().toggleCodeBlock().run();
            return;
        }

        const selectedText = editor.state.doc.textBetween(from, to, '\n', '\n').replace(/\r\n/g, '\n');

        editor
            .chain()
            .focus()
            .insertContentAt(
                { from, to },
                {
                    type: 'codeBlock',
                    content: selectedText.length > 0 ? [{ type: 'text', text: selectedText }] : [],
                },
            )
            .run();
    }

    insertCollapsibleSection(): void {
        const editor = this.editor;
        if (!editor) {
            return;
        }

        const { from, to, empty } = editor.state.selection;
        if (empty) {
            editor.chain().focus().insertCollapsibleSection().run();
            return;
        }

        const selectedText = editor.state.doc.textBetween(from, to, '\n', '\n');
        const normalized = selectedText.replace(/\r\n/g, '\n');
        const lines = normalized.split('\n');

        const titleText = (lines[0] || '').trim() || 'Click to expand';
        const selectedSlice = editor.state.doc.slice(from, to);
        const bodyFragment = this.buildCollapsibleBodyFragment(selectedSlice.content);
        const bodyContent = bodyFragment.childCount > 0
            ? bodyFragment.toJSON()
            : [{ type: 'paragraph' }];

        editor
            .chain()
            .focus()
            .insertContentAt(
                { from, to },
                {
                    type: 'collapsibleSection',
                    attrs: { open: true, collapsibleId: this.createCollapsibleId() },
                    content: [
                        {
                            type: 'collapsibleTitle',
                            content: [{ type: 'text', text: titleText }],
                        },
                        {
                            type: 'collapsibleContent',
                            content: bodyContent,
                        },
                    ],
                },
            )
            .run();
    }

    private buildCollapsibleBodyFragment(content: Fragment): Fragment {
        const nodes: ProseMirrorNode[] = [];
        content.forEach((child) => nodes.push(child));

        if (nodes.length === 0) {
            return Fragment.empty;
        }

        const result: ProseMirrorNode[] = [];
        let firstLineConsumed = false;

        for (const node of nodes) {
            if (!firstLineConsumed) {
                if (node.isTextblock) {
                    const stripped = this.stripFirstLineFromTextblock(node);
                    firstLineConsumed = true;
                    if (stripped) {
                        result.push(stripped);
                    }
                    continue;
                }

                // If selection starts with a non-text block (e.g. table/list),
                // that first block is considered the title source and omitted.
                firstLineConsumed = true;
                continue;
            }

            result.push(node);
        }

        return result.length > 0 ? Fragment.fromArray(result) : Fragment.empty;
    }

    private stripFirstLineFromTextblock(node: ProseMirrorNode): ProseMirrorNode | null {
        if (!node.isTextblock) {
            return node;
        }

        const keptChildren: ProseMirrorNode[] = [];
        let firstLineEnded = false;

        node.content.forEach((child) => {
            if (!firstLineEnded) {
                if (child.type.name === 'hardBreak') {
                    firstLineEnded = true;
                    return;
                }

                if (child.isText) {
                    const text = child.text || '';
                    const newlineIndex = text.indexOf('\n');

                    if (newlineIndex >= 0) {
                        firstLineEnded = true;
                        const remainder = text.slice(newlineIndex + 1);
                        if (remainder.length > 0) {
                            keptChildren.push(child.type.schema.text(remainder, child.marks));
                        }
                    }
                    return;
                }

                // Inline non-text nodes inside the first line are dropped from
                // body because title must be plain text.
                return;
            }

            keptChildren.push(child);
        });

        // No line break in this textblock means it was fully consumed as title.
        if (!firstLineEnded) {
            return null;
        }

        return node.type.create(node.attrs, Fragment.fromArray(keptChildren), node.marks);
    }

    undo(): void {
        this.editor?.chain().focus().undo().run();
    }

    redo(): void {
        this.editor?.chain().focus().redo().run();
    }

    insertTable(): void {
        this.editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    }

    addRow(): void {
        this.editor?.chain().focus().addRowAfter().run();
    }

    addColumn(): void {
        this.editor?.chain().focus().addColumnAfter().run();
    }

    insertRowAbove(): void {
        this.editor?.chain().focus().addRowBefore().run();
        this.isTableMenuOpen = false;
    }

    insertRowBelow(): void {
        this.editor?.chain().focus().addRowAfter().run();
        this.isTableMenuOpen = false;
    }

    insertColumnLeft(): void {
        this.editor?.chain().focus().addColumnBefore().run();
        this.isTableMenuOpen = false;
    }

    insertColumnRight(): void {
        this.editor?.chain().focus().addColumnAfter().run();
        this.isTableMenuOpen = false;
    }

    deleteRow(): void {
        this.editor?.chain().focus().deleteRow().run();
        this.isTableMenuOpen = false;
    }

    deleteColumn(): void {
        this.editor?.chain().focus().deleteColumn().run();
        this.isTableMenuOpen = false;
    }

    deleteTable(): void {
        this.editor?.chain().focus().deleteTable().run();
        this.isTableMenuOpen = false;
    }

    toggleTableMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isTableMenuOpen = !this.isTableMenuOpen;
    }

    // Text color handler
    onTextColorSelected(event: ColorSelectedEvent): void {
        if (event.hex === null || event.source === 'clear') {
            this.editor?.chain().focus().unsetColor().run();
            this.currentTextColor = null;
        } else {
            this.editor?.chain().focus().setColor(event.hex).run();
            this.currentTextColor = event.hex;
        }
    }

    // Background color handler
    onBackgroundColorSelected(event: ColorSelectedEvent): void {
        if (event.hex === null || event.source === 'clear') {
            this.editor?.chain().focus().unsetBackgroundColor().run();
            this.currentBackgroundColor = null;
        } else {
            this.editor?.chain().focus().setBackgroundColor(event.hex).run();
            this.currentBackgroundColor = event.hex;
        }
    }

    clearFormatting(): void {
        const editor = this.editor;
        if (!editor) {
            return;
        }

        const { from, to } = editor.state.selection;
        const chain = editor.chain().focus().setTextSelection({ from, to });

        // Clear inline styling only. Do not clear nodes because that can unwrap
        // content out of collapsible/list structures.
        chain
            .unsetAllMarks()
            .unsetColor()
            .unsetBackgroundColor()
            .unsetFontSize()
            .unsetFontFamily()
            .setTextAlign('left');

        if (editor.isActive('codeBlock')) {
            chain.toggleCodeBlock();
        }

        if (editor.isActive('heading') || editor.isActive('blockquote')) {
            chain.setParagraph();
        }

        chain.run();

        // Reset block typography to default body text (e.g. 12px from editor defaults).
        editor.commands.unsetBlockFontSize();
    }

    cleanupFormatting(): void {
        if (!this.editor) return;

        const view = this.editor.view;
        const sel = this.editor.state.selection;

        // Scope: use the selection range when text is selected, otherwise the whole doc.
        const rangeFrom = sel.empty ? 0                   : sel.from;
        const rangeTo   = sel.empty ? this.editor.state.doc.content.size : sel.to;

        let currentFrom = rangeFrom;
        let currentTo = rangeTo;

        // Pass 0: run the HTML cleanup pipeline (font-size cleanup + blank-line normalization)
        // against the same scope used by this command.
        const originalHtml = this.serializeRangeToHtml(currentFrom, currentTo);
        const cleanedHtml = this.cleanupHtmlFragment(originalHtml);
        if (cleanedHtml !== originalHtml) {
            const normalizedState = this.editor.state;
            const parser = ProseMirrorDOMParser.fromSchema(normalizedState.schema);
            const wrapper = document.createElement('div');
            wrapper.innerHTML = cleanedHtml;

            const normalizedSlice = parser.parseSlice(wrapper, { preserveWhitespace: false });
            const normalizeTr = normalizedState.tr.replace(currentFrom, currentTo, normalizedSlice);
            currentFrom = normalizeTr.mapping.map(currentFrom, -1);
            currentTo = normalizeTr.mapping.map(currentTo, 1);
            view.dispatch(normalizeTr);
        }

        const state = this.editor.state;

        const toReplaceWithBlank: Array<{ from: number; to: number }> = [];
        this.collectHorizontalRuleRanges(state.doc, 0, currentFrom, currentTo, toReplaceWithBlank);

        // Pass 1: replace horizontal rules with a single empty paragraph.
        let mappedFrom = currentFrom;
        let mappedTo = currentTo;
        if (toReplaceWithBlank.length > 0) {
            const paragraphNode = state.schema.nodes['paragraph']?.create();
            if (paragraphNode) {
                let replaceTr = state.tr;
                toReplaceWithBlank.sort((a, b) => b.from - a.from);
                for (const { from, to } of toReplaceWithBlank) {
                    replaceTr = replaceTr.replaceWith(from, to, paragraphNode);
                }
                mappedFrom = replaceTr.mapping.map(currentFrom, -1);
                mappedTo = replaceTr.mapping.map(currentTo, 1);
                view.dispatch(replaceTr);
            }
        }

        const toDelete: Array<{ from: number; to: number }> = [];
        const postReplaceState = this.editor.state;
        this.collectExcessBlankParagraphs(postReplaceState.doc, 0, mappedFrom, mappedTo, toDelete);

        let finalFrom = mappedFrom;
        let finalTo = mappedTo;

        if (toDelete.length > 0) {
            const tr = postReplaceState.tr;
            // Delete in reverse order so earlier positions are not invalidated.
            toDelete.sort((a, b) => b.from - a.from);
            for (const { from, to } of toDelete) {
                tr.delete(from, to);
            }
            finalFrom = tr.mapping.map(mappedFrom, -1);
            finalTo = tr.mapping.map(mappedTo, 1);
            view.dispatch(tr);
        }

        this.applyCleanupTableHeaderBackground(finalFrom, finalTo);
    }

    private applyCleanupTableHeaderBackground(rangeFrom: number, rangeTo: number): void {
        if (!this.editor) {
            return;
        }

        const state = this.editor.state;
        const tr = state.tr;
        let changed = false;

        state.doc.descendants((node, pos) => {
            if (node.type.name !== 'table' || node.childCount === 0) {
                return;
            }

            const tableFrom = pos;
            const tableTo = pos + node.nodeSize;
            const overlapsSelectionRange = tableFrom < rangeTo && tableTo > rangeFrom;
            if (!overlapsSelectionRange) {
                return;
            }

            const firstRow = node.child(0);
            const rowPos = pos + 1;

            firstRow.forEach((cellNode, cellOffset) => {
                const currentColor = typeof cellNode.attrs?.['backgroundColor'] === 'string'
                    ? cellNode.attrs['backgroundColor'].trim().toLowerCase()
                    : '';
                if (currentColor === CLEANUP_TABLE_HEADER_BACKGROUND) {
                    return;
                }

                const cellPos = rowPos + 1 + cellOffset;
                tr.setNodeMarkup(cellPos, undefined, {
                    ...cellNode.attrs,
                    backgroundColor: CLEANUP_TABLE_HEADER_BACKGROUND,
                });
                changed = true;
            });
        });

        if (changed) {
            this.editor.view.dispatch(tr);
        }
    }

    /** Collect in-range horizontal-rule nodes so they can be replaced by blank lines. */
    private collectHorizontalRuleRanges(
        node: ProseMirrorNode,
        contentStart: number,
        rangeFrom: number,
        rangeTo: number,
        out: Array<{ from: number; to: number }>,
    ): void {
        interface Entry { node: ProseMirrorNode; from: number; to: number }
        const children: Entry[] = [];

        node.forEach((child, offset) => {
            const from = contentStart + offset;
            children.push({ node: child, from, to: from + child.nodeSize });
        });

        for (const { node: child, from, to } of children) {
            const inRange = from < rangeTo && to > rangeFrom;
            if (child.type.name === 'horizontalRule' && inRange) {
                out.push({ from, to });
                continue;
            }

            if (child.isBlock && child.childCount > 0) {
                this.collectHorizontalRuleRanges(child, from + 1, rangeFrom, rangeTo, out);
            }
        }
    }

    /**
     * Recursively walks the document and marks the 2nd, 3rd, … consecutive
     * blank paragraphs in each run for deletion, keeping exactly one blank
     * paragraph per run.  Only nodes whose range overlaps [rangeFrom, rangeTo]
     * are considered.
     */
    private collectExcessBlankParagraphs(
        node: ProseMirrorNode,
        contentStart: number,
        rangeFrom: number,
        rangeTo: number,
        out: Array<{ from: number; to: number }>,
    ): void {
        interface Entry { node: ProseMirrorNode; from: number; to: number }
        const children: Entry[] = [];

        node.forEach((child, offset) => {
            const from = contentStart + offset;
            children.push({ node: child, from, to: from + child.nodeSize });
        });

        // Recurse into nested block containers first (e.g. blockquotes, list items).
        for (const { node: child, from } of children) {
            if (child.isBlock && child.type.name !== 'paragraph' && child.childCount > 0) {
                this.collectExcessBlankParagraphs(child, from + 1, rangeFrom, rangeTo, out);
            }
        }

        // Walk direct children, tracking runs of consecutive blank paragraphs.
        // Within a run, keep the first blank and delete the rest.
        let consecutiveBlanks = 0;
        for (const { node: child, from, to } of children) {
            // A node is in-scope when it overlaps with [rangeFrom, rangeTo].
            const inRange = from < rangeTo && to > rangeFrom;

            if (child.type.name === 'paragraph' && this.isPmNodeBlank(child)) {
                if (inRange) {
                    consecutiveBlanks++;
                    if (consecutiveBlanks > 1) {
                        // Second or later blank in a run — delete it.
                        out.push({ from, to });
                    }
                    // First blank in a run is kept; no push.
                } else {
                    // Out-of-range blank still counts toward the run so that
                    // consecutive blanks straddling the range boundary are handled
                    // consistently.
                    consecutiveBlanks++;
                }
            } else {
                consecutiveBlanks = 0;
            }
        }
    }

    /** Returns true if the paragraph node contains no visible content. */
    private isPmNodeBlank(node: ProseMirrorNode): boolean {
        if (node.type.name !== 'paragraph') return false;
        if (node.childCount === 0) return true;
        let hasContent = false;
        node.forEach((child) => {
            if (child.type.name === 'hardBreak') return;
            const text = child.isText ? (child.text ?? '') : child.textContent;
            if (text.trim().length > 0) {
                hasContent = true;
            }
        });
        return !hasContent;
    }

    async pasteAsPlainText(): Promise<void> {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                this.editor?.chain().focus().insertContent(text).run();
            }
        } catch (err) {
            console.error('Failed to read clipboard:', err);
        }
    }

    clearCellBackgroundColor(): void {
        if (!this.editor) { return; }
        const { state, view } = this.editor;
        const { selection } = state;
        if (selection instanceof CellSelection) {
            const tr = state.tr;
            selection.forEachCell((cell, pos) => {
                const attrs = { ...cell.attrs, backgroundColor: null };
                tr.setNodeMarkup(pos, undefined, attrs);
            });
            if (tr.docChanged) { view.dispatch(tr); }
        } else {
            this.editor.chain().focus().setCellAttribute('backgroundColor', null).run();
        }
        this.isTableMenuOpen = false;
    }

    setCellBackgroundColor(color: string): void {
        if (!this.editor) {
            return;
        }
        const { state, view } = this.editor;
        const { selection } = state;

        if (selection instanceof CellSelection) {
            const tr = state.tr;
            selection.forEachCell((cell, pos) => {
                const attrs = { ...cell.attrs, backgroundColor: color };
                tr.setNodeMarkup(pos, undefined, attrs);
            });
            if (tr.docChanged) {
                view.dispatch(tr);
            }
        } else {
            this.editor.chain().focus().setCellAttribute('backgroundColor', color).run();
        }
        this.isTableMenuOpen = false;
    }

    setAlign(align: 'left' | 'center' | 'right' | 'justify') {
        this.editor?.chain().focus().setTextAlign(align).run();
    }

    private serializeRangeToHtml(from: number, to: number): string {
        if (!this.editor || typeof document === 'undefined') {
            return '';
        }

        const fragment = this.editor.state.doc.slice(from, to).content;
        const serializer = DOMSerializer.fromSchema(this.editor.state.schema);
        const container = document.createElement('div');
        container.appendChild(serializer.serializeFragment(fragment));
        return container.innerHTML;
    }

    private cleanupHtmlFragment(html: string): string {
        if (typeof document === 'undefined' || !html) {
            return html;
        }

        const container = document.createElement('div');
        container.innerHTML = html;

        this.stripFontSizeFormatting(container);
        this.normalizeNonBreakingSpaces(container);
        this.normalizeBlankLineElements(container);
        this.ensureBlankLineBeforeHeadings(container);

        return container.innerHTML;
    }

    private normalizeNonBreakingSpaces(root: HTMLElement): void {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const textNodes: Text[] = [];
        let node: Node | null;
        while ((node = walker.nextNode())) {
            textNodes.push(node as Text);
        }

        textNodes.forEach((textNode) => {
            if (textNode.nodeValue && textNode.nodeValue.indexOf('\u00A0') !== -1) {
                textNode.nodeValue = textNode.nodeValue.replace(/\u00A0/g, ' ');
            }
        });
    }

    private stripFontSizeFormatting(root: HTMLElement): void {
        const styledElements = root.querySelectorAll<HTMLElement>('[style], font[size]');

        styledElements.forEach((element) => {
            if (element.hasAttribute('style')) {
                const nextStyle = (element.getAttribute('style') || '')
                    .replace(/(?:^|;)\s*font-size\s*:[^;]+;?/gi, ';')
                    .replace(/;\s*;/g, ';')
                    .replace(/^\s*;|;\s*$/g, '')
                    .trim();

                if (nextStyle) {
                    element.setAttribute('style', nextStyle);
                } else {
                    element.removeAttribute('style');
                }
            }

            if (element.tagName === 'FONT') {
                element.removeAttribute('size');
            }
        });
    }

    private normalizeBlankLineElements(root: HTMLElement): void {
        const skipParentTags = new Set(['TD', 'TH', 'LI', 'PRE', 'CODE']);

        const visit = (parent: Element | HTMLElement): void => {
            const childElements = Array.from(parent.children) as HTMLElement[];
            childElements.forEach((child) => visit(child));

            if (skipParentTags.has((parent as HTMLElement).tagName)) {
                return;
            }

            const liveChildren = Array.from(parent.children) as HTMLElement[];
            let sawContent = false;
            let previousWasBlank = false;
            let previousContentElement: HTMLElement | null = null;

            for (const child of liveChildren) {
                if (!child.parentElement || child.parentElement !== parent) {
                    continue;
                }

                if (this.isBlankLineElement(child)) {
                    const followsColon = previousContentElement !== null && this.getElementText(previousContentElement).endsWith(':');

                    // Remove leading blanks, duplicate blanks, and blank lines after headings ending with ':'.
                    if (!sawContent || previousWasBlank || followsColon) {
                        child.remove();
                        continue;
                    }

                    previousWasBlank = true;
                    continue;
                }

                if (this.hasVisibleContent(child)) {
                    sawContent = true;
                    previousWasBlank = false;
                    previousContentElement = child;
                }
            }

            // Remove trailing blank lines at the end of each container.
            let tail = parent.lastElementChild as HTMLElement | null;
            while (tail && this.isBlankLineElement(tail)) {
                const previous = tail.previousElementSibling as HTMLElement | null;
                tail.remove();
                tail = previous;
            }
        };

        visit(root);
    }

    private ensureBlankLineBeforeHeadings(root: HTMLElement): void {
        const headings = Array.from(root.querySelectorAll('h1, h2, h3')) as HTMLElement[];

        headings.forEach((heading) => {
            const parent = heading.parentElement;
            if (!parent) {
                return;
            }

            // Collapse any run of blank-line elements immediately before the heading
            // down to just the one closest to it.
            let prev = heading.previousElementSibling as HTMLElement | null;
            let keptOne = false;
            while (prev && this.isBlankLineElement(prev)) {
                const earlier = prev.previousElementSibling as HTMLElement | null;
                if (keptOne) {
                    prev.remove();
                } else {
                    keptOne = true;
                }
                prev = earlier;
            }

            // If the heading isn't the first element in its container and has no
            // blank line directly before it, insert one.
            const immediatePrev = heading.previousElementSibling as HTMLElement | null;
            if (immediatePrev && !this.isBlankLineElement(immediatePrev)) {
                parent.insertBefore(document.createElement('p'), heading);
            }
        });
    }

    private isBlankLineElement(element: HTMLElement): boolean {
        const tagName = element.tagName.toLowerCase();
        if (!['p', 'div'].includes(tagName)) {
            return false;
        }

        if (element.querySelector('img,table,pre,code,ul,ol,li,blockquote,hr')) {
            return false;
        }

        return this.getElementText(element) === '';
    }

    private hasVisibleContent(element: HTMLElement): boolean {
        if (this.getElementText(element) !== '') {
            return true;
        }

        return !!element.querySelector('img,table,pre,code,hr');
    }

    private getElementText(element: HTMLElement): string {
        return (element.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    }

    isAlign(align: 'left' | 'center' | 'right' | 'justify') {
        return !!this.editor?.isActive({ textAlign: align });
    }

    setFontFamily(value: string): void {
        if (!value) {
            this.editor?.chain().focus().unsetFontFamily().run();
            this.currentFontFamily = '';
        } else {
            this.editor?.chain().focus().setFontFamily(value).run();
            this.currentFontFamily = value;
        }
    }

    setFontSize(size: string) {
        if (!size) {
            this.editor?.chain().focus().unsetFontSize().run();
            this.editor?.commands.unsetBlockFontSize();
            this.currentFontSize = '';
            this.hasExplicitFontSize = false;
            return;
        }
        const numeric = Number(size);
        if (!Number.isFinite(numeric) || numeric <= 0) {
            return;
        }

        const normalizedPx = `${numeric}px`;
        const zoomAware = this.toZoomAwareFontSize(normalizedPx);
        this.editor?.chain().focus().setFontSize(zoomAware).run();
        this.editor?.commands.setBlockFontSize(normalizedPx);
        this.currentFontSize = String(numeric);
        this.hasExplicitFontSize = true;
    }

    private extractFontSizePx(value: string): number | null {
        const trimmed = (value || '').trim();
        const match = trimmed.match(/(\d+(?:\.\d+)?)px/i);
        if (!match) {
            return null;
        }
        const parsed = Number(match[1]);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private toZoomAwareFontSize(pxFontSize: string): string {
        return `calc(${pxFontSize} * ${this.zoomAwareFontSizeVar})`;
    }

    private normalizeInlineFontSizesForZoom(): void {
        if (!this.editor) {
            return;
        }

        const { state } = this.editor;
        const tr = state.tr;
        let changed = false;

        state.doc.descendants((node, pos) => {
            if (!node.isText || node.marks.length === 0) {
                return true;
            }

            for (const mark of node.marks) {
                if (mark.type.name !== 'textStyle') {
                    continue;
                }

                const raw = mark.attrs['fontSize'];
                if (typeof raw !== 'string') {
                    continue;
                }

                const px = this.extractFontSizePx(raw);
                if (px === null) {
                    continue;
                }

                const nextFontSize = this.toZoomAwareFontSize(`${px}px`);
                if (raw.trim() === nextFontSize) {
                    continue;
                }

                const nextMark = mark.type.create({
                    ...mark.attrs,
                    fontSize: nextFontSize,
                });

                tr.removeMark(pos, pos + node.nodeSize, mark);
                tr.addMark(pos, pos + node.nodeSize, nextMark);
                changed = true;
            }

            return true;
        });

        if (changed) {
            this.editor.view.dispatch(tr);
        }
    }

    insertLink(): void {
        this.openLinkDialog();
    }

    openLinkDialog(): void {
        if (!this.editor) {
            return;
        }

        const { from, to } = this.editor.state.selection;
        const selectedText = this.editor.state.doc.textBetween(from, to, '');
        const currentLink = this.editor.getAttributes('link')['href'] as string | undefined;

        this.linkSelection = { from, to };
        this.linkUrl = currentLink || 'https://';
        this.linkLabel = selectedText || currentLink || '';
        this.linkDialogVisible = true;
    }

    onLinkDialogConfirm(): void {
        if (!this.editor) {
            return;
        }

        const url = this.linkUrl.trim();
        const label = this.linkLabel.trim();
        if (!url || !label) {
            return;
        }

        const selection = this.linkSelection;
        const chain = this.editor.chain().focus();
        if (selection) {
            chain.setTextSelection(selection);
        }

        chain
            .insertContent({
                type: 'text',
                text: label,
                marks: [{ type: 'link', attrs: { href: url } }],
            })
            .run();

        this.closeLinkDialog();
    }

    onLinkDialogCancel(): void {
        this.closeLinkDialog();
    }

    private closeLinkDialog(): void {
        this.linkDialogVisible = false;
        this.linkLabel = '';
        this.linkUrl = 'https://';
        this.linkSelection = null;
    }

    setHeadingLevel(level: string) {
        if (!this.editor) {
            return;
        }
        const parsed = Number(level);

        // Only the selected line(s) change — never the following line (see isolateSelectedLines).
        const isolateLines = ({ tr }: { tr: Transaction }) => {
            isolateSelectedLines(tr);
            return true;
        };

        if (!parsed) {
            this.editor.chain().focus().command(isolateLines).unsetFontSize().setParagraph().run();
            this.editor.commands.unsetBlockFontSize();
            this.currentFontSize = '';
            this.currentHeadingLevel = '';
            this.hasExplicitFontSize = false;
            return;
        }
        if (parsed < 1 || parsed > 6) {
            return;
        }
        this.editor
            .chain()
            .focus()
            .command(isolateLines)
            .unsetFontSize()
            .setHeading({ level: parsed as 1 | 2 | 3 | 4 | 5 | 6 })
            .run();
        this.editor.commands.unsetBlockFontSize();
        this.currentFontSize = '';
        this.currentHeadingLevel = String(parsed);
        this.hasExplicitFontSize = false;
    }

    // Note Link Methods

    openNoteLinkDialog(): void {
        if (!this.editor) {
            return;
        }

        // Get selected text
        const { from, to } = this.editor.state.selection;
        const selectedText = this.editor.state.doc.textBetween(from, to, '');
        this.selectedTextForNoteLink = selectedText;

        // Load all notes
        this.notesLoading = true;
        this.noteLinkDialogVisible = true;

        this.dataService.getAllNotes().subscribe({
            next: (response) => {
                this.allNotes = response.data || [];
                this.notesLoading = false;
            },
            error: (error) => {
                console.error('Failed to load notes:', error);
                this.allNotes = [];
                this.notesLoading = false;
            },
        });
    }

    onNoteLinkConfirm(event: { noteId: string; linkText: string; noteTitle: string }): void {
        if (!this.editor) {
            return;
        }

        const { noteId, linkText } = event;

        // Insert or replace selected text with note link
        this.editor
            .chain()
            .focus()
            .insertContent({
                type: 'text',
                text: linkText,
                marks: [
                    {
                        type: 'noteLink',
                        attrs: { noteId, title: event.noteTitle },
                    },
                ],
            })
            .run();

        this.noteLinkInserted.emit(noteId);
        this.noteLinkDialogVisible = false;
    }

    onNoteLinkCancel(): void {
        this.noteLinkDialogVisible = false;
    }

    navigateToNote(noteId: string): void {
        // Navigate using Angular Router
        this.router.navigate(['/view', noteId]);
    }

    private applyLegacyLinkStyling(): void {
        const legacyLinks = this.editorHost?.nativeElement?.querySelectorAll('a.note-link');
        if (!legacyLinks) return;
        
        legacyLinks.forEach((link: Element) => {
            const htmlLink = link as HTMLElement;
            const href = htmlLink.getAttribute('href') || '';
            const idFromHrefMatch = href.match(/(?:^|\/)view(?:2)?\/([^/?#]+)/i);
            const idFromHref = idFromHrefMatch?.[1] ? decodeURIComponent(idFromHrefMatch[1]) : '';
            const noteId = (htmlLink.getAttribute('data-note-id') || idFromHref || '').trim();

            if (noteId) {
                htmlLink.setAttribute('data-note-id', noteId);
                htmlLink.setAttribute('href', `/view/${encodeURIComponent(noteId)}`);
            }

            htmlLink.style.color = '#2563eb';      // blue-600
            htmlLink.style.textDecoration = 'underline';
            htmlLink.style.cursor = 'pointer';
            htmlLink.style.transition = 'color 0.2s';
            htmlLink.style.fontWeight = '500';
        });
    }

    openImageInsertDialog(): void {
        this.imageInsertDialogVisible = true;
    }

    onImageInsertLinked(payload: LinkedImageInsertPayload): void {
        this.editor?.chain().focus().insertLinkedImage(payload).run();
    }

    onImageInsertAttachment(payload: AttachmentImageInsertPayload): void {
        if (this.editor?.chain().focus().insertAttachmentImage(payload).run()) {
            this.attachmentImageInserted.emit(payload);
        }
    }

    // ------------------------------------------------------------------
    // Image drag & drop (extensions/image-drop.ts)
    // ------------------------------------------------------------------

    private fileDragDepth = 0;

    private isFileDrag(event: DragEvent): boolean {
        return Array.from(event.dataTransfer?.types ?? []).includes('Files');
    }

    private setFileDragOver(active: boolean): void {
        this.editorHost.nativeElement.classList.toggle('is-file-dragover', active);
    }

    private readonly onFileDragEnter = (event: DragEvent): void => {
        if (this.readOnly || !this.isFileDrag(event)) return;
        this.fileDragDepth++;
        this.setFileDragOver(true);
    };

    private readonly onFileDragOver = (event: DragEvent): void => {
        if (this.readOnly || !this.isFileDrag(event)) return;
        // Allow the drop (and show the copy cursor) so it reaches the editor instead of the browser.
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    private readonly onFileDragLeave = (event: DragEvent): void => {
        if (!this.isFileDrag(event)) return;
        this.fileDragDepth = Math.max(0, this.fileDragDepth - 1);
        if (this.fileDragDepth === 0) this.setFileDragOver(false);
    };

    /** Drops inside the document are handled (and stopped) by ImageDrop; this only sees files dropped
     * on the editor's padding, and keeps the browser from opening them in place of the app. */
    private readonly onFileDropFallback = (event: DragEvent): void => {
        this.fileDragDepth = 0;
        this.setFileDragOver(false);
        if (this.isFileDrag(event)) event.preventDefault();
    };

    private getImageDropHandlers(): ImageDropHandlers | null {
        // ImageDrop stops the drop event, so the host's drop listener never sees it — reset the drag state here.
        this.fileDragDepth = 0;
        this.setFileDragOver(false);
        if (this.readOnly) return null;
        const noteId = (this.noteId ?? '').trim();
        if (!noteId) {
            this.ngZone.run(() => this.snackbarService.show('Images can only be added to a saved note.', 'error'));
            return null;
        }
        const spaceId = this.spaceId ?? undefined;
        return {
            upload: async (file: File) => {
                const meta = await firstValueFrom(this.imageService.uploadImage(file, noteId, spaceId));
                // Display from the dropped file itself: no fetch, and no race with the upload's
                // (best-effort, async) IndexedDB caching of the same blob.
                if (!this.attachmentImageObjectUrls.has(meta.attachmentId)) {
                    this.attachmentImageObjectUrls.set(meta.attachmentId, URL.createObjectURL(file));
                }
                return {
                    attachmentId: meta.attachmentId,
                    src: meta.src,
                    filename: meta.filename ?? file.name,
                    fileSizeBytes: meta.fileSizeBytes ?? file.size,
                    mimeType: meta.mimeType ?? file.type,
                };
            },
            onInserted: (payload) => this.ngZone.run(() => this.attachmentImageInserted.emit(payload)),
            onError: (message) => this.ngZone.run(() => this.snackbarService.show(message, 'error')),
        };
    }

    /** Removes every image showing the given attachment (the attachment was deleted). A normal,
     * undoable edit, so the host's form picks it up through contentUpdated. Returns how many went. */
    removeAttachmentImages(attachmentId: string): number {
        const editor = this.editor;
        if (!editor || editor.isDestroyed || !attachmentId) return 0;
        const { tr } = editor.state;
        let removed = 0;
        editor.state.doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs['attachmentId'] === attachmentId) {
                tr.delete(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize));
                removed++;
            }
        });
        if (removed > 0) {
            this.closeImageNodeMenu();
            editor.view.dispatch(tr);
        }
        return removed;
    }

    /** A queued attachment (offline / unsaved note) was uploaded and got its server id: repoint the
     * image nodes still using the temporary id, so the next save doesn't write a dead reference. */
    private rewriteAttachmentImageId(oldId: string, newId: string): void {
        const editor = this.editor;
        if (!editor || editor.isDestroyed || !oldId || !newId) return;
        const { tr } = editor.state;
        editor.state.doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs['attachmentId'] === oldId) {
                tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    attachmentId: newId,
                    src: replaceAttachmentIdInHtml(String(node.attrs['src'] ?? ''), oldId, newId),
                });
            }
        });
        if (!tr.docChanged) return;

        const objectUrl = this.attachmentImageObjectUrls.get(oldId);
        if (objectUrl) {
            this.attachmentImageObjectUrls.delete(oldId);
            this.attachmentImageObjectUrls.set(newId, objectUrl);
        }
        editor.view.dispatch(tr.setMeta('addToHistory', false).setMeta('isAttachmentIdRewrite', true));
    }

    onImageEditSave(attrs: ImageEditAttrs): void {
        if (!this.editor) return;
        const chain = this.editor.chain().focus();
        if (this.imageEditPos !== null) {
            chain.setNodeSelection(this.imageEditPos).updateAttributes('image', attrs).run();
        } else {
            chain.updateAttributes('image', attrs).run();
        }
        this.imageEditPos = null;
        this.closeImageNodeMenu();
    }

    private openImageEditDialog(node: ProseMirrorNode, pos: number): void {
        this.ngZone.run(() => {
            this.imageEditPos = pos;
            this.imageEditAttrs = { ...node.attrs } as ImageEditAttrs;
            this.imageEditNaturalWidth = Number(node.attrs['naturalWidth']) || null;
            this.imageEditNaturalHeight = Number(node.attrs['naturalHeight']) || null;
            this.imageEditFileSizeBytes = null;
            this.imageEditFilename = null;
            this.imageEditDialogVisible = true;

            const attachmentId = String(node.attrs['attachmentId'] ?? '').trim();
            if (attachmentId) {
                this.imageService.getImageMetadata(attachmentId, this.spaceId ?? undefined, this.noteId ?? undefined).subscribe({
                    next: (meta) => {
                        this.ngZone.run(() => {
                            this.imageEditFilename = meta?.filename ?? '';
                            if (meta) {
                                this.imageEditNaturalWidth = meta.naturalWidth ?? null;
                                this.imageEditNaturalHeight = meta.naturalHeight ?? null;
                                this.imageEditFileSizeBytes = meta.fileSizeBytes ?? null;
                            }
                        });
                    },
                    error: () => this.ngZone.run(() => (this.imageEditFilename = '')),
                });
            }
        });
    }

    private handleEditorClick(event: MouseEvent): void {
        const editor = this.editor;
        const target = event.target as HTMLElement | null;
        if (!editor || !target) {
            return;
        }

        const legacyLink = target.closest('a.note-link');
        if (legacyLink && !editor.isEditable) {
            const noteId = legacyLink.getAttribute('data-note-id');
            if (noteId) {
                event.preventDefault();
                event.stopPropagation();
                this.router.navigate(['/view', noteId]);
                return;
            }
        }

        if (!editor.isEditable || this.readOnly || event.button !== 0) {
            return;
        }

        // Open edit dialog when clicking an image node
        const imgEl = target.closest('img') as HTMLImageElement | null;
        if (imgEl) {
            const view = editor.view;
            const pos = view.posAtDOM(imgEl, 0);
            if (pos !== null && pos >= 0) {
                const resolvedNode = view.state.doc.nodeAt(pos);
                if (resolvedNode && resolvedNode.type.name === 'image') {
                    event.preventDefault();
                    event.stopPropagation();
                    editor.chain().focus().setNodeSelection(pos).run();
                    return;
                }
            }
        }

        const host = this.editorHost.nativeElement;
        const proseMirror = host.classList.contains('ProseMirror')
            ? host
            : (host.querySelector('.ProseMirror') as HTMLElement | null);
        if (!proseMirror || !proseMirror.contains(target)) {
            return;
        }

        if (target.closest('.column-resize-handle')) {
            return;
        }

        const lastElement = proseMirror.lastElementChild as HTMLElement | null;
        const lastBottom = lastElement?.getBoundingClientRect().bottom ?? proseMirror.getBoundingClientRect().top;
        const posAtClick = editor.view.posAtCoords({ left: event.clientX, top: event.clientY });

        if (event.clientY >= lastBottom - 1 || !posAtClick) {
            requestAnimationFrame(() => {
                const endPos = editor.state.doc.content.size;
                editor.chain().focus().setTextSelection(endPos).run();
            });
        }
    }

    private scheduleAttachmentImageRefresh(): void {
        if (this.attachmentImageRefreshScheduled) {
            return;
        }

        this.attachmentImageRefreshScheduled = true;
        requestAnimationFrame(() => {
            this.attachmentImageRefreshScheduled = false;
            void this.refreshAttachmentImageSources();
        });
    }

    private async refreshAttachmentImageSources(): Promise<void> {
        const host = this.editorHost?.nativeElement;
        if (!host) {
            return;
        }

        const imageElements = Array.from(
            host.querySelectorAll('img[data-image-source-type="attachment"][data-attachment-id]')
        ) as HTMLImageElement[];

        for (const imageElement of imageElements) {
            const attachmentId = String(imageElement.getAttribute('data-attachment-id') ?? '').trim();
            if (!attachmentId) {
                continue;
            }

            const existingObjectUrl = this.attachmentImageObjectUrls.get(attachmentId);
            if (existingObjectUrl) {
                if (imageElement.src !== existingObjectUrl) {
                    imageElement.src = existingObjectUrl;
                }
                continue;
            }

            if (this.attachmentImageRequests.has(attachmentId)) {
                continue;
            }

            this.attachmentImageRequests.add(attachmentId);
            this.fileUploadService
                .getFileForNote(this.noteId ?? '', attachmentId, undefined, this.spaceId ?? undefined)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (blob) => {
                        this.attachmentImageRequests.delete(attachmentId);
                        this.attachmentImageLoadErrorsShown.delete(attachmentId);
                        if (!(blob instanceof Blob) || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
                            return;
                        }

                        const objectUrl = URL.createObjectURL(blob);
                        const previousUrl = this.attachmentImageObjectUrls.get(attachmentId);
                        if (previousUrl && previousUrl !== objectUrl) {
                            URL.revokeObjectURL(previousUrl);
                        }

                        this.attachmentImageObjectUrls.set(attachmentId, objectUrl);

                        const matchingImages = host.querySelectorAll(`img[data-image-source-type="attachment"][data-attachment-id="${attachmentId}"]`);
                        matchingImages.forEach((node) => {
                            (node as HTMLImageElement).src = objectUrl;
                        });
                    },
                    error: (err) => {
                        this.attachmentImageRequests.delete(attachmentId);

                        // Offline, or the server is unreachable — status 0 covers both a
                        // genuine network failure and the httpTimeoutInterceptor's
                        // synthetic timeout error, while 502/503/504 are what a reverse
                        // proxy in front of the Flask server returns when Flask itself is
                        // down/not responding (seen consistently on production hosts, e.g.
                        // iOS Safari hitting https://goul.org directly instead of via the
                        // dev proxy) — this is expected and must be silent either way. The
                        // image will retry automatically once connectivity returns (see the
                        // online$ subscription in ngAfterViewInit); the "server not
                        // responding" state is already surfaced via the footer status.
                        const status = err?.status;
                        const isConnectivityError =
                            !this.environmentService.isOnline() ||
                            status === 0 ||
                            status === 502 ||
                            status === 503 ||
                            status === 504;
                        if (isConnectivityError) {
                            return;
                        }

                        if (!this.attachmentImageLoadErrorsShown.has(attachmentId)) {
                            this.attachmentImageLoadErrorsShown.add(attachmentId);
                            const detail = err?.error?.message || err?.message || err?.statusText || 'unknown error';
                            this.snackbarService.show(`Couldn't load an embedded image (${detail}).`, 'error');
                        }
                    },
                });
        }
    }

    private revokeAttachmentImageObjectUrls(): void {
        if (typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') {
            this.attachmentImageObjectUrls.clear();
            return;
        }

        this.attachmentImageObjectUrls.forEach((objectUrl) => {
            URL.revokeObjectURL(objectUrl);
        });
        this.attachmentImageObjectUrls.clear();
    }

    // ── Markdown conversion ───────────────────────────────────────────────────

    convertSelectionMarkdown(): void {
        if (!this.editor) return;
        const { from, to } = this.editor.state.selection;
        if (from === to) return;
        const selectedText = this.editor.state.doc.textBetween(from, to, '\n');
        if (!selectedText.trim()) return;
        const html = this.mdToHtml(selectedText);
        this.editor.chain().focus().insertContent(html).run();
    }

    private mdToHtml(md: string): string {
        const lines = md.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
        const out: string[] = [];
        let i = 0;

        while (i < lines.length) {
            const line = lines[i];
            const t = line.trim();

            if (t === '') { i++; continue; }

            // Fenced code block
            if (/^```/.test(t)) {
                const lang = t.slice(3).trim();
                i++;
                const code: string[] = [];
                while (i < lines.length && !lines[i].trim().startsWith('```')) {
                    code.push(lines[i]);
                    i++;
                }
                i++; // closing ```
                out.push(`<pre><code class="language-${this.mdEsc(lang)}">${this.mdEsc(code.join('\n'))}</code></pre>`);
                continue;
            }

            // Heading
            const hm = t.match(/^(#{1,6})\s+(.*)/);
            if (hm) {
                const lvl = hm[1].length;
                out.push(`<h${lvl}>${this.mdInline(hm[2])}</h${lvl}>`);
                i++;
                continue;
            }

            // Horizontal rule (--- / *** / ___)
            if (/^[-]{3,}$|^[*]{3,}$|^[_]{3,}$/.test(t.replace(/\s/g, ''))) {
                out.push('<hr>');
                i++;
                continue;
            }

            // Blockquote
            if (/^>\s?/.test(t)) {
                const qLines: string[] = [];
                while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
                    qLines.push(lines[i].trim().replace(/^>\s?/, ''));
                    i++;
                }
                out.push(`<blockquote>${this.mdToHtml(qLines.join('\n'))}</blockquote>`);
                continue;
            }

            // Table (header row followed by separator row)
            if (/^\|.+\|/.test(t) && i + 1 < lines.length && /^\|[\s\-:|]+\|/.test(lines[i + 1].trim())) {
                const heads = this.mdTableRow(t);
                i += 2; // skip header + separator
                let tbl = '<table><thead><tr>' + heads.map(h => `<th>${this.mdInline(h)}</th>`).join('') + '</tr></thead><tbody>';
                while (i < lines.length && /^\|.+\|/.test(lines[i].trim())) {
                    const cells = this.mdTableRow(lines[i].trim());
                    tbl += '<tr>' + Array.from({ length: Math.max(cells.length, heads.length) }, (_, ci) =>
                        `<td>${this.mdInline((cells[ci] ?? '').trim())}</td>`).join('') + '</tr>';
                    i++;
                }
                out.push(tbl + '</tbody></table>');
                continue;
            }

            // List
            if (this.mdIsList(t)) {
                const { html, next } = this.mdParseList(lines, i, this.mdIndent(line));
                out.push(html);
                i = next;
                continue;
            }

            // Paragraph — collect until blank line or block-level element
            const pLines: string[] = [];
            while (i < lines.length) {
                const pt = lines[i].trim();
                if (pt === '' || this.mdIsBlock(pt)) break;
                pLines.push(lines[i]);
                i++;
            }
            if (pLines.length > 0) {
                out.push(`<p>${pLines.map(l => this.mdInline(l)).join('<br>')}</p>`);
            }
        }

        return out.join('');
    }

    private mdParseList(lines: string[], i: number, baseIndent: number): { html: string; next: number } {
        const ft = lines[i].trim();
        const isTask = /^[-*+]\s\[[ xX]\]\s/.test(ft);
        const isOrdered = /^\d+\.\s/.test(ft);
        const tag = isOrdered ? 'ol' : 'ul';
        const dataAttr = isTask ? ' data-type="taskList"' : '';
        let html = `<${tag}${dataAttr}>`;

        while (i < lines.length) {
            const line = lines[i];
            const t = line.trim();
            const indent = this.mdIndent(line);

            if (t === '' || indent < baseIndent || !this.mdIsList(t)) break;
            if (indent > baseIndent) break; // belongs to a nested list, caller handles it

            // Stop if item type doesn't match the list type (prevents - items from
            // being swallowed into an <ol> or vice versa)
            const curIsOrdered = /^\d+\.\s/.test(t);
            if (curIsOrdered !== isOrdered) break;

            // Task list item
            const taskM = t.match(/^[-*+]\s\[([ xX])\]\s(.*)/);
            if (taskM) {
                const checked = taskM[1].toLowerCase() === 'x';
                i++;
                let sub = '';
                if (i < lines.length && this.mdIndent(lines[i]) > indent && this.mdIsList(lines[i].trim())) {
                    const r = this.mdParseList(lines, i, this.mdIndent(lines[i]));
                    sub = r.html; i = r.next;
                }
                html += `<li data-type="taskItem" data-checked="${checked}"><p>${this.mdInline(taskM[2])}</p>${sub}</li>`;
                continue;
            }

            // Regular list item
            const itemM = t.match(/^(?:[-*+]|\d+\.)\s+(.*)/);
            if (!itemM) { i++; continue; }

            i++;
            let sub = '';
            if (i < lines.length && this.mdIndent(lines[i]) > indent && this.mdIsList(lines[i].trim())) {
                const r = this.mdParseList(lines, i, this.mdIndent(lines[i]));
                sub = r.html; i = r.next;
            }
            html += `<li><p>${this.mdInline(itemM[1])}</p>${sub}</li>`;
        }

        html += `</${tag}>`;
        return { html, next: i };
    }

    private mdIsList(t: string): boolean {
        return /^[-*+]\s/.test(t) || /^\d+\.\s/.test(t);
    }

    private mdIsBlock(t: string): boolean {
        return /^#{1,6}\s/.test(t) ||
            /^```/.test(t) ||
            /^[-]{3,}$|^[*]{3,}$|^[_]{3,}$/.test(t.replace(/\s/g, '')) ||
            /^>\s?/.test(t) ||
            /^\|.+\|/.test(t) ||
            this.mdIsList(t);
    }

    private mdIndent(line: string): number {
        return (line.match(/^(\s*)/) ?? ['', ''])[1].length;
    }

    private mdTableRow(row: string): string[] {
        return row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
    }

    private mdEsc(text: string): string {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    private mdInline(text: string): string {
        // Bold + italic combined
        text = text.replace(/\*{3}(.+?)\*{3}/g, '<strong><em>$1</em></strong>');
        text = text.replace(/_{3}(.+?)_{3}/g, '<strong><em>$1</em></strong>');
        // Bold
        text = text.replace(/\*{2}(.+?)\*{2}/g, '<strong>$1</strong>');
        text = text.replace(/_{2}(.+?)_{2}/g, '<strong>$1</strong>');
        // Italic
        text = text.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
        text = text.replace(/_([^_\n]+)_/g, '<em>$1</em>');
        // Strikethrough
        text = text.replace(/~~(.+?)~~/g, '<s>$1</s>');
        // Inline code (escape HTML inside)
        text = text.replace(/`([^`\n]+)`/g, (_, c) => `<code>${this.mdEsc(c)}</code>`);
        // Images (before links to prevent mis-parse)
        text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, url) =>
            `<img src="${url}" alt="${this.mdEsc(alt)}">`);
        // Links
        text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) =>
            `<a href="${url}">${label}</a>`);
        return text;
    }
}
